import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReactDOM from "react-dom";
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaRedo,
  FaPrint,
  FaCheck,
  FaFileAlt,
  FaBuilding,
  FaFileContract,
  FaFileExcel,
  FaPaperclip,
} from "react-icons/fa";
import {
  getProgressBills,
  deleteProgressBill,
  getProgressBillDetails,
  approveProgressBill,
  getProgressBillJournalEntries,
} from "../../../../redux/Slices/ProgressBillSlice";
import { getContracts } from "../../../../redux/Slices/ContractSlice";
import { getProjects } from "../../../../redux/Slices/ProjectsSlice";
import { getCustomers } from "../../../../redux/Slices/CustomerSlice";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
  getCurrentExecutionQty,
  getPreviouslyExecutedQty,
} from "../../../../shared/utils.js/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import { useTranslation } from "react-i18next";
import {
  isOnline,
  saveProgressBillCache,
  getProgressBillCache,
  generateProgressBillsCacheKey,
  getAllProgressBillsCacheForQuery,
  getAllContractsCacheForQuery,
  saveDropdownCache,
  generateDropdownCacheKey,
  getDropdownCache,
  getPendingRequests,
  saveProgressBillDetailCache,
  getProgressBillDetailCache,
} from "../../../../shared/utils.js/offlineQueue";
import {
  makeOfflineRequest,
  checkAndRetryPendingRequests,
} from "../../../../shared/utils.js/offlineRequestManager";
import { getReadableError } from "../../../../shared/utils.js/permissionUtils";

const normalizeListResponse = payload => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  // Check if data.data exists (nested structure)
  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    // Check for pagination in data object (current_page, last_page directly in data)
    if (
      payload.data.current_page !== undefined ||
      payload.data.last_page !== undefined
    ) {
      pagination = {
        current_page: payload.data.current_page,
        last_page: payload.data.last_page,
        per_page: payload.data.per_page,
        total: payload.data.total,
      };
    } else {
      pagination = payload.data?.meta || payload.data?.pagination || null;
    }
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    // Check if pagination info is directly in payload
    if (payload.current_page !== undefined || payload.last_page !== undefined) {
      pagination = {
        current_page: payload.current_page,
        last_page: payload.last_page,
        per_page: payload.per_page,
        total: payload.total,
      };
    } else {
      pagination = payload.meta || payload.pagination || null;
    }
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const getPreviouslyExecutedQtyFromItem = getPreviouslyExecutedQty;

const getCurrentQtyFromItem = getCurrentExecutionQty;

const getTotalQty = item =>
  parseFloat(
    item.total_qty ??
      item.total_executed_qty ??
      item.executed_qty ??
      item.qty ??
      item.quantity ??
      0,
  );
const getItemHasAnyExecution = item => getTotalQty(item) > 0;

const normalizeItem = item => {
  return {
    ...item,
    total_qty: getTotalQty(item),
    previous_qty: getPreviouslyExecutedQtyFromItem(item),
    amount: parseFloat(item.amount ?? item.total_amount ?? 0),
    contract_item: {
      ...(item.contract_item || {}),
      id: item.contract_item?.id || item.id || item.contract_item_id,
      quantity: item.contract_item?.quantity || item.contract_qty || 0,
      unit_price: item.contract_item?.unit_price || item.unit_price || 0,
      description_ar:
        item.contract_item?.description_ar ||
        item.description ||
        item.description_ar,
      description_en:
        item.contract_item?.description_en ||
        item.description ||
        item.description_en,
    },
  };
};

const buildExecutionMap = async (
  contractId,
  currentBillId,
  token,
  currentLang,
  currentBillDate,
) => {
  const map = {};
  try {
    const allResp = await axios.get(`${BASE_URL}/progress-bills`, {
      params: { contract_id: contractId, per_page: 1000 },
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      },
    });
    const allBills = allResp.data?.data?.data || allResp.data?.data || [];
    const currentDate = currentBillDate
      ? new Date(currentBillDate).getTime()
      : Infinity;
    for (const bill of allBills) {
      if (String(bill.id) === String(currentBillId)) continue;
      if (bill.status !== "approved" && bill.status !== "paid") continue;
      const billDate = new Date(
        bill.created_at || bill.date || bill.bill_date,
      ).getTime();
      if (currentDate !== Infinity && billDate >= currentDate) continue;
      try {
        const detailResp = await axios.get(
          `${BASE_URL}/progress-bills/${bill.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Accept-Language": currentLang,
            },
          },
        );
        const billData = detailResp.data?.data;
        if (billData?.items) {
          for (const item of billData.items) {
            const cid =
              item.contract_item_id || item.contract_item?.id || item.id;
            if (cid) {
              map[String(cid)] =
                (map[String(cid)] || 0) +
                parseFloat(item.total_qty ?? item.total_executed_qty ?? 0);
            }
          }
        }
      } catch (e) {
        console.warn("[execMap] skip bill", bill.id, e);
      }
    }
  } catch (e) {
    console.warn("[execMap] failed", e);
  }
  return map;
};

const mergeContractItems = async (
  details,
  token,
  currentLang,
  options = {},
) => {
  if (!details?.contract_id || !Array.isArray(details.items)) return details;
  const originalBillItemIds = new Set(
    (details.items || []).map(i =>
      String(i.contract_item_id || i.contract_item?.id || i.id),
    ),
  );
  try {
    const contractResp = await axios.get(
      `${BASE_URL}/progress-bills/contract/${details.contract_id}/details`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      },
    );
    const contractData = contractResp.data?.data;
    if (!contractData?.items?.length) return details;

    const billItemMap = {};
    details.items.forEach(bi => {
      const cid = bi.contract_item_id || bi.contract_item?.id || bi.id;
      if (cid) billItemMap[String(cid)] = bi;
    });

    const execMap = await buildExecutionMap(
      details.contract_id,
      details.id,
      token,
      currentLang,
      details.created_at,
    );

    details.items = contractData.items.map(ci => {
      const ciId = ci.id || ci.contract_item_id;
      const match = billItemMap[String(ciId)];
      const prevTotal = execMap[String(ciId)] || 0;

      if (match) {
        return normalizeItem({ ...ci, ...match });
      }

      if (prevTotal > 0) {
        return normalizeItem({
          ...ci,
          id: ci.id,
          contract_item_id: ci.id,
          current_qty: 0,
          previous_qty: prevTotal,
          total_qty: prevTotal,
          amount: prevTotal * parseFloat(ci.unit_price || 0),
        });
      }

      return normalizeItem({
        ...ci,
        id: ci.id,
        contract_item_id: ci.id,
        current_qty: 0,
        total_qty: 0,
      });
    });

    const summary = Array.isArray(details.invoice_summary)
      ? details.invoice_summary
      : [];
    const isRunning =
      options.isRunning ??
      (details.bill_category
        ? details.bill_category === "running"
        : summary.length > 0
          ? !summary.some(
              r => r.key === "previous_payments" && parseFloat(r.value) > 0,
            )
          : details.type !== "bill");

    if (isRunning) {
      details.items = details.items.filter(item => {
        const itemId = String(
          item.contract_item_id || item.contract_item?.id || item.id,
        );
        return originalBillItemIds.has(itemId) || getTotalQty(item) > 0;
      });
    }
  } catch (e) {
    console.error("Failed to merge contract items:", e);
  }
  return details;
};

const ProgressBill = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const [progressBills, setProgressBills] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [perPageInput, setPerPageInput] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");
  const [contractId, setContractId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [billCategoryFilter, setBillCategoryFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [billToApprove, setBillToApprove] = useState(null);
  const [billForPrint, setBillForPrint] = useState(null);
  const [billDetailsForPrint, setBillDetailsForPrint] = useState(null);
  const [isGeneratingBill, setIsGeneratingBill] = useState(false);
  const [isGeneratingPdfAll, setIsGeneratingPdfAll] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [exportProgressBills, setExportProgressBills] = useState([]);
  const [approvingBillId, setApprovingBillId] = useState(null);
  const [journalEntriesModal, setJournalEntriesModal] = useState({
    open: false,
    billId: null,
    entries: null,
    loading: false,
    billData: null,
  });
  const [projectModal, setProjectModal] = useState({
    open: false,
    project: null,
  });
  const [contractModal, setContractModal] = useState({
    open: false,
    contract: null,
  });
  const [attachmentsModal, setAttachmentsModal] = useState({
    open: false,
    attachments: [],
    billId: null,
  });
  const [previewBill, setPreviewBill] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const previewContainerRef = useRef(null);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;
  const backgroundLogo = `topHeader.png`;

  // Contract dropdown
  const [contractDropdownOpen, setContractDropdownOpen] = useState(false);
  const [contractSearchTerm, setContractSearchTerm] = useState("");
  const [contractOptions, setContractOptions] = useState([]);
  const [contractPage, setContractPage] = useState(1);
  const [contractPagination, setContractPagination] = useState(null);
  const contractSearchInputRef = useRef(null);

  // Project dropdown
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPagination, setProjectPagination] = useState(null);
  const projectSearchInputRef = useRef(null);

  // Customer dropdown
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPagination, setCustomerPagination] = useState(null);
  const customerSearchInputRef = useRef(null);
  const isInitialMount = useRef(true);

  const paginationInfo = useMemo(() => {
    if (!pagination) return { current_page: currentPage, last_page: 1 };
    return {
      current_page: pagination.current_page || pagination.page || currentPage,
      last_page: pagination.last_page || pagination.total_pages || 1,
    };
  }, [pagination, currentPage]);

  const activeFilterLabels = useMemo(() => {
    const labels = [];
    if (searchTerm) labels.push(`بحث: ${searchTerm}`);
    if (contractId) labels.push("عقد محدد");
    if (projectId) labels.push("مشروع محدد");
    if (customerId) labels.push("عميل محدد");
    if (statusFilter) labels.push("حالة محددة");
    if (typeFilter) labels.push("نوع محدد");
    if (billCategoryFilter) labels.push("فئة محددة");
    if (dateFrom) labels.push(`من: ${dateFrom}`);
    if (dateTo) labels.push(`إلى: ${dateTo}`);
    return labels;
  }, [
    searchTerm,
    contractId,
    projectId,
    customerId,
    statusFilter,
    typeFilter,
    billCategoryFilter,
    dateFrom,
    dateTo,
  ]);

  const hasActiveFilters = activeFilterLabels.length > 0;

  const buildProgressBillsFilters = useCallback(
    (overrides = {}, includeFilters = true) => ({
      ...(includeFilters
        ? {
            search: searchTerm || undefined,
            contract_id: contractId || undefined,
            project_id: projectId || undefined,
            customer_id: customerId || undefined,
            status: statusFilter || undefined,
            type: typeFilter || undefined,
            bill_category: billCategoryFilter || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          }
        : {}),
      ...overrides,
    }),
    [
      searchTerm,
      contractId,
      projectId,
      customerId,
      statusFilter,
      typeFilter,
      billCategoryFilter,
      dateFrom,
      dateTo,
    ],
  );

  const chooseExportScope = useCallback(
    actionLabel => {
      if (!hasActiveFilters) return true;
      return window.confirm(
        `${actionLabel}\n\nيوجد فلاتر نشطة:\n${activeFilterLabels.join("\n")}\n\nاضغط موافق لاستخدام الفلاتر الحالية.\nاضغط إلغاء لاستخراج كل البيانات بدون فلاتر.`,
      );
    },
    [activeFilterLabels, hasActiveFilters],
  );

  const fetchAllProgressBillsForCurrentFilters = useCallback(
    async (includeFilters = true) => {
      const baseParams = buildProgressBillsFilters(
        { per_page: 1000 },
        includeFilters,
      );

      if (!isOnline()) {
        const cached = await getAllProgressBillsCacheForQuery(baseParams);
        if (Array.isArray(cached)) return cached;
        if (Array.isArray(cached?.items)) return cached.items;
        return progressBills || [];
      }

      const collected = [];
      let page = 1;
      let lastPage = 1;

      do {
        const payload = await dispatch(
          getProgressBills({ ...baseParams, page }),
        ).unwrap();
        const { items, pagination: paginationMeta } =
          normalizeListResponse(payload);
        collected.push(...items);

        lastPage = Number(
          paginationMeta?.last_page || paginationMeta?.total_pages || page,
        );
        page += 1;
      } while (page <= lastPage);

      const seen = new Set();
      return collected.filter(item => {
        const key =
          item?.id ?? `${item?.bill_no || ""}-${item?.invoice_number || ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    [buildProgressBillsFilters, dispatch, progressBills],
  );

  const fetchProgressBills = useCallback(
    async (
      page = currentPage,
      search = searchTerm,
      contract = contractId,
      status = statusFilter,
      project = projectId,
      customer = customerId,
      type = typeFilter,
      billCategory = billCategoryFilter,
      fromDate = dateFrom,
      toDate = dateTo,
      sort = sortBy,
      order = sortOrder,
    ) => {
      try {
        setIsLoading(true);

        const params = {
          page,
          per_page: perPage,
          search: search || undefined,
          contract_id: contract || undefined,
          project_id: project || undefined,
          customer_id: customer || undefined,
          status: status || undefined,
          type: type || undefined,
          bill_category: billCategory || undefined,
          date_from: fromDate || undefined,
          date_to: toDate || undefined,
          sort_by: sort || undefined,
          sort_order: order || undefined,
        };
        const cacheKey = generateProgressBillsCacheKey(params);

        let pendingAdds = [];
        let pendingEdits = new Map();
        let pendingDeletes = new Set();

        // Check pending requests regardless of online status for consistent UI
        try {
          const requests = await getPendingRequests();
          requests.forEach(req => {
            // Add Progress Bill: POST /contracts/:id/progress-bills
            if (
              req.method === "POST" &&
              req.url.includes("/progress-bills") &&
              !req.url.includes("/approve")
            ) {
              const contractIdMatch = req.url.match(
                /\/contracts\/(\d+)\/progress-bills/,
              );
              if (contractIdMatch) {
                const data = req.serializedData?.data || {};
                // Normalize formData structure (contracts usually sent as field, here it's in URL)
                const newBill = {
                  id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  status: "draft",
                  contract_id: contractIdMatch[1],
                  // Fill fields from data
                  ...data,
                  date: new Date().toISOString(),
                  pending: true, // Marker for UI
                };
                pendingAdds.unshift(newBill);
              }
            }
            // Edit Progress Bill: PUT /contracts/progress-bills/:id
            if (
              req.method === "PUT" &&
              req.url.includes("/contracts/progress-bills/")
            ) {
              const idMatch = req.url.match(
                /\/contracts\/progress-bills\/(\d+)/,
              );
              if (idMatch) {
                const id = Number(idMatch[1]);
                const data = req.serializedData?.data || {};
                pendingEdits.set(id, { ...data, pending: true });
              }
            }
            // Delete Progress Bill
            if (
              req.method === "DELETE" &&
              req.url.includes("/contracts/progress-bills/")
            ) {
              const idMatch = req.url.match(
                /\/contracts\/progress-bills\/(\d+)/,
              );
              if (idMatch) {
                pendingDeletes.add(Number(idMatch[1]));
              }
            }
          });
        } catch (e) {
          console.error("Error reading pending requests", e);
        }

        // Try cache first if offline
        if (!isOnline()) {
          try {
            let cachedItems = [];
            let cachedPagination = null;

            const cached = await getProgressBillCache(cacheKey);
            if (cached) {
              cachedItems = cached.progressBills;
              cachedPagination = cached.pagination;
            } else {
              // Fallback to all cached
              const allCached = await getAllProgressBillsCacheForQuery(params);
              if (allCached) {
                cachedItems = allCached;
                cachedPagination = {
                  current_page: 1,
                  last_page: 1,
                  per_page: allCached.length,
                  total: allCached.length,
                };
              }
            }

            // Merge Pending
            let mergedItems = [...cachedItems];

            // Apply Edits
            mergedItems = mergedItems.map(item => {
              if (pendingEdits.has(item.id)) {
                return { ...item, ...pendingEdits.get(item.id) };
              }
              return item;
            });

            // Apply Deletes
            mergedItems = mergedItems.filter(
              item => !pendingDeletes.has(item.id),
            );

            // Apply Adds (prepend)
            // Filter adds based on current filters?
            // Only if we want to be strict. For now, show them to give feedback.
            if (pendingAdds.length > 0) {
              if (contract) {
                pendingAdds = pendingAdds.filter(
                  b => String(b.contract_id) === String(contract),
                );
              }
              mergedItems = [...pendingAdds, ...mergedItems];
            }

            setProgressBills(mergedItems);
            setPagination(
              cachedPagination || {
                current_page: 1,
                last_page: 1,
                per_page: mergedItems.length,
                total: mergedItems.length,
              },
            );
          } catch (error) {
            console.error("Offline cache fetch error:", error);
          }
          if (!progressBills.length && pendingAdds.length === 0) {
            // Optional toast, maybe too spammy if just switching pages
          }
          return;
        }

        const payload = await dispatch(getProgressBills(params)).unwrap();
        const { items, pagination: paginationMeta } =
          normalizeListResponse(payload);

        // Save to cache
        await saveProgressBillCache(cacheKey, items, paginationMeta);

        // We could also merge pending requests here for "optimistic" view even while online but not yet synced
        // But usually online means sync happens fast. The ServiceWorker/BackgroundSync should handle it.
        // However, if sync is slow, showing pending items is good UX.

        // Merge Pending (Online case)
        let mergedItemsOnline = [...items];

        mergedItemsOnline = mergedItemsOnline.map(item => {
          if (pendingEdits.has(item.id)) {
            return { ...item, ...pendingEdits.get(item.id) };
          }
          return item;
        });

        mergedItemsOnline = mergedItemsOnline.filter(
          item => !pendingDeletes.has(item.id),
        );

        // For adds, we only show them if they aren't already returning from server (which they shouldn't be if strictly pending)
        // But if sync finished, they might be in `items`.
        // We rely on `getPendingRequests` returning truly pending ones.
        if (pendingAdds.length > 0) {
          if (contract) {
            pendingAdds = pendingAdds.filter(
              b => String(b.contract_id) === String(contract),
            );
          }
          mergedItemsOnline = [...pendingAdds, ...mergedItemsOnline];
        }

        setProgressBills(mergedItemsOnline);
        setPagination(paginationMeta);
      } catch (error) {
        if (!isOnline()) return; // Already handled
        toast.error(
          getReadableError(error, t("progressBills.errors.fetchError")),
          { rtl: isRTL },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      dispatch,
      currentPage,
      searchTerm,
      contractId,
      statusFilter,
      projectId,
      customerId,
      typeFilter,
      billCategoryFilter,
      dateFrom,
      dateTo,
      sortBy,
      sortOrder,
      perPage,
      t,
      isRTL,
    ],
  );

  useEffect(() => {
    setTitle("sidenav.progressBills");
  }, [setTitle]);

  // على التحديث (Refresh): امسح أي params تمت إضافتها من الفلتر من الـ URL
  useEffect(() => {
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUrlFromFilters = useCallback(() => {
    const next = new URLSearchParams();
    if (contractId) next.set("contract_id", contractId);
    if (projectId) next.set("project_id", projectId);
    if (customerId) next.set("customer_id", customerId);
    if (statusFilter) next.set("status", statusFilter);
    if (typeFilter) next.set("type", typeFilter);
    if (billCategoryFilter) next.set("bill_category", billCategoryFilter);
    if (searchTerm) next.set("search", searchTerm);
    if (dateFrom) next.set("date_from", dateFrom);
    if (dateTo) next.set("date_to", dateTo);
    setSearchParams(next, { replace: true });
  }, [
    contractId,
    projectId,
    customerId,
    statusFilter,
    typeFilter,
    billCategoryFilter,
    searchTerm,
    dateFrom,
    dateTo,
    setSearchParams,
  ]);

  // Close dropdowns when clicking outside or when opening another dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      const isClickInsideDropdown = event.target.closest(
        "[data-dropdown-container]",
      );
      const isClickOnDropdownToggle = event.target.closest(
        "[data-dropdown-toggle]",
      );

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setContractDropdownOpen(false);
        setProjectDropdownOpen(false);
        setCustomerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchProgressBills(
      currentPage,
      searchTerm,
      contractId,
      statusFilter,
      projectId,
      customerId,
      typeFilter,
      billCategoryFilter,
      dateFrom,
      dateTo,
      sortBy,
      sortOrder,
    );
    if (!isInitialMount.current) {
      updateUrlFromFilters();
    } else {
      isInitialMount.current = false;
    }
  }, [
    currentPage,
    searchTerm,
    contractId,
    statusFilter,
    projectId,
    customerId,
    typeFilter,
    billCategoryFilter,
    dateFrom,
    dateTo,
    sortBy,
    sortOrder,
    perPage,
    fetchProgressBills,
    updateUrlFromFilters,
  ]);

  const fetchContracts = useCallback(
    async (page = contractPage, search = contractSearchTerm) => {
      try {
        const queryParams = { page, per_page: 10, search: search || undefined };

        if (!isOnline()) {
          const cachedResult = await getAllContractsCacheForQuery(queryParams);
          const cachedItems = cachedResult?.items || [];
          if (cachedItems && cachedItems.length > 0) {
            setContractOptions(cachedItems);
            setContractPagination({
              current_page: page,
              last_page: 1,
              per_page: 10,
              total: cachedItems.length,
            });
            return;
          }
        }

        const token = getToken();
        if (!token) throw new Error(t("progressBills.errors.noAuthToken"));
        const currentLang = getCurrentLanguage();
        const response = await axios.get(`${BASE_URL}/contracts`, {
          params: queryParams,
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(
          response.data,
        );
        setContractOptions(items);
        setContractPagination(paginationMeta);
      } catch (error) {
        if (!isOnline()) return;
        toast.error(
          getReadableError(
            error,
            t("progressBills.errors.fetchContractsError"),
          ),
          { rtl: isRTL },
        );
      }
    },
    [contractPage, contractSearchTerm, t, isRTL],
  );

  useEffect(() => {
    if (contractDropdownOpen) {
      fetchContracts(contractPage, contractSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractDropdownOpen, contractPage]);

  useEffect(() => {
    if (contractSearchTerm !== undefined && contractDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setContractPage(1);
        fetchContracts(1, contractSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractSearchTerm, contractDropdownOpen]);

  const fetchProjects = useCallback(
    async (page = projectPage, search = projectSearchTerm) => {
      try {
        const result = await dispatch(
          getProjects({ page, per_page: 10, search: search || undefined }),
        ).unwrap();
        const payload = result?.data ?? result;
        const { items, pagination: paginationMeta } =
          normalizeListResponse(payload);
        setProjectOptions(items || []);
        setProjectPagination(paginationMeta);
      } catch (error) {
        if (!isOnline()) return;
        toast.error(
          getReadableError(error, t("progressBills.errors.fetchError")),
          { rtl: isRTL },
        );
      }
    },
    [dispatch, projectPage, projectSearchTerm, t, isRTL],
  );

  useEffect(() => {
    if (projectDropdownOpen) {
      fetchProjects(projectPage, projectSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDropdownOpen, projectPage]);

  useEffect(() => {
    if (projectSearchTerm !== undefined && projectDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setProjectPage(1);
        fetchProjects(1, projectSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSearchTerm, projectDropdownOpen]);

  const fetchCustomers = useCallback(
    async (page = customerPage, search = customerSearchTerm) => {
      try {
        const payload = await dispatch(
          getCustomers({ page, per_page: 10, search: search || undefined }),
        ).unwrap();
        const { items, pagination: paginationMeta } =
          normalizeListResponse(payload);
        setCustomerOptions(items || []);
        setCustomerPagination(paginationMeta);
      } catch (error) {
        if (!isOnline()) return;
        toast.error(
          getReadableError(error, t("progressBills.errors.fetchError")),
          { rtl: isRTL },
        );
      }
    },
    [dispatch, customerPage, customerSearchTerm, t, isRTL],
  );

  useEffect(() => {
    if (customerDropdownOpen) {
      fetchCustomers(customerPage, customerSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDropdownOpen, customerPage]);

  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setCustomerPage(1);
        fetchCustomers(1, customerSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchTerm, customerDropdownOpen]);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm("");
    setContractId("");
    setProjectId("");
    setCustomerId("");
    setStatusFilter("");
    setTypeFilter("");
    setBillCategoryFilter("");
    setDateFrom("");
    setDateTo("");
    setSortBy("");
    setSortOrder("desc");
    setCurrentPage(1);
    setContractSearchTerm("");
    setContractPage(1);
    setProjectSearchTerm("");
    setProjectPage(1);
    setCustomerSearchTerm("");
    setCustomerPage(1);
    setSearchParams({}, { replace: true });
  };

  const handleSort = column => {
    if (sortBy === column) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      setCurrentPage(1);
      fetchProgressBills(
        1,
        searchTerm,
        contractId,
        statusFilter,
        projectId,
        customerId,
        typeFilter,
        billCategoryFilter,
        dateFrom,
        dateTo,
        column,
        newOrder,
      );
    } else {
      setSortBy(column);
      setSortOrder("asc");
      setCurrentPage(1);
      fetchProgressBills(
        1,
        searchTerm,
        contractId,
        statusFilter,
        projectId,
        customerId,
        typeFilter,
        billCategoryFilter,
        dateFrom,
        dateTo,
        column,
        "asc",
      );
    }
  };

  const getSortIcon = column =>
    sortBy !== column ? " ↕" : sortOrder === "asc" ? " ↑" : " ↓";

  const handleDelete = async () => {
    if (!billToDelete) return;
    const idToDelete = billToDelete.id;
    const billToDeleteBackup = { ...billToDelete }; // Deep copy for backup

    // Check if bill can be deleted (only draft or rejected can be deleted)
    const canDelete =
      billToDelete.status === "draft" || billToDelete.status === "rejected";

    // Only do optimistic delete if the bill can actually be deleted
    if (canDelete) {
      // Optimistically remove from local state first
      setProgressBills(prev => prev.filter(bill => bill.id !== idToDelete));
    }

    setDeleteModalOpen(false);
    setBillToDelete(null);

    if (!isOnline()) {
      try {
        await makeOfflineRequest(
          `/contracts/progress-bills/${idToDelete}`,
          "DELETE",
        );
        toast.info(
          t("progressBills.success.offlineDeleted", {
            defaultValue: "Deleted locally, will sync when online",
          }),
          { rtl: isRTL },
        );
        return;
      } catch (error) {
        if (error.message === "OFFLINE_REQUEST_SAVED") {
          toast.info(
            t("progressBills.success.offlineDeleted", {
              defaultValue: "Deleted locally, will sync when online",
            }),
            { rtl: isRTL },
          );
          return;
        }
        // Fallback to error handling below
        console.error("Offline delete error", error);
      }
    }

    try {
      const result = await dispatch(deleteProgressBill(idToDelete));
      // Check if the action was rejected
      if (deleteProgressBill.rejected.match(result)) {
        throw new Error(result.payload || "فشل حذف فاتورة التقدم");
      }
      toast.success(t("progressBills.success.deleted"), { rtl: isRTL });
      // No need to fetch - already optimistically updated (if we did optimistic delete)
    } catch (error) {
      // Revert optimistic update on error - restore the bill (only if we did optimistic delete)
      if (canDelete) {
        setProgressBills(prev => {
          // Check if bill already exists (shouldn't, but be safe)
          const exists = prev.find(bill => bill.id === idToDelete);
          if (!exists && billToDeleteBackup) {
            // Insert back in the same position if possible, otherwise at the end
            const newList = [...prev, billToDeleteBackup];
            // Try to maintain order by sorting if IDs are numeric
            return newList.sort((a, b) => {
              if (a.id && b.id) {
                if (typeof a.id === "number" && typeof b.id === "number") {
                  return a.id - b.id;
                }
                return String(a.id).localeCompare(String(b.id));
              }
              return 0;
            });
          }
          return prev;
        });
      }

      // Show error message
      toast.error(
        getReadableError(error, t("progressBills.errors.deleteError")),
        { rtl: isRTL },
      );
    }
  };

  const handleApprove = async () => {
    if (!billToApprove) return;

    // Optimistic update
    const previousBills = [...progressBills];
    const billId = billToApprove.id;
    setProgressBills(prev =>
      prev.map(bill =>
        bill.id === billId ? { ...bill, status: "approved" } : bill,
      ),
    );
    setApprovingBillId(billId);
    setApproveModalOpen(false);
    setBillToApprove(null);

    if (!isOnline()) {
      try {
        await makeOfflineRequest(`/progress-bills/${billId}/approve`, "POST");
        toast.info(
          t("progressBills.success.offlineApproved", {
            defaultValue: "Approved locally, will sync when online",
          }),
          { rtl: isRTL },
        );
        setApprovingBillId(null);
        return;
      } catch (error) {
        console.error("Offline approve error", error);
        // Revert optimistic update
        setProgressBills(previousBills);
        if (error.message === "OFFLINE_REQUEST_SAVED") {
          // Actually this is success for offline
          // But makeOfflineRequest throws this error?
          // In AddProgressBill, we check: if (offlineError.message === 'OFFLINE_REQUEST_SAVED') ...
          // Yes, it throws to interrupt normal flow.
          setProgressBills(prev =>
            prev.map(bill =>
              bill.id === billId ? { ...bill, status: "approved" } : bill,
            ),
          );
          toast.info(
            t("progressBills.success.offlineApproved", {
              defaultValue: "Approved locally, will sync when online",
            }),
            { rtl: isRTL },
          );
        } else {
          toast.error(t("progressBills.errors.offlineError"), { rtl: isRTL });
        }
        setApprovingBillId(null);
        return;
      }
    }

    try {
      await dispatch(approveProgressBill(billId)).unwrap();
      toast.success(t("progressBills.success.approved"), { rtl: isRTL });
      fetchProgressBills(currentPage);
    } catch (error) {
      // Revert optimistic update
      setProgressBills(previousBills);
      toast.error(
        getReadableError(error, t("progressBills.errors.approveError")),
        { rtl: isRTL },
      );
    } finally {
      setApprovingBillId(null);
    }
  };

  const handleShowJournalEntries = async bill => {
    try {
      setJournalEntriesModal({
        open: true,
        billId: bill.id,
        entries: null,
        loading: true,
        billData: null,
      });

      let details = null;
      let entries = [];

      // Offline: Try detailed cache first
      if (!isOnline()) {
        try {
          // Try specific details cache first (contains full payload including journal entries)
          const cachedDetail = await getProgressBillDetailCache(bill.id);
          if (cachedDetail) {
            details = cachedDetail;
          } else {
            // Fallback to list cache
            const allCached = await getAllProgressBillsCacheForQuery({
              per_page: 1000,
            });
            details = allCached.find(b => String(b.id) === String(bill.id));
          }
        } catch (e) {
          console.error(e);
        }
      }

      if (isOnline()) {
        try {
          // Fetch details from the main endpoint
          const currentLanguage = i18n.language || "ar";
          const payload = await dispatch(
            getProgressBillDetails({ id: bill.id, language: currentLanguage }),
          ).unwrap();

          // Save to detailed cache for offline use
          if (payload) {
            saveProgressBillDetailCache(bill.id, payload).catch(e =>
              console.error("Cache save error", e),
            );
          }

          // Attempt to locate the object containing journal_entry in possible locations
          const candidates = [payload?.data?.data, payload?.data, payload];

          for (const candidate of candidates) {
            if (
              candidate &&
              (candidate.journal_entry || candidate.journal_entries)
            ) {
              details = candidate;
              const val = candidate.journal_entry || candidate.journal_entries;
              entries = Array.isArray(val) ? val : val ? [val] : [];
              break;
            }
          }

          // If data fetched but no journal entries found, still use it as details
          if (!details) {
            details = payload?.data || payload;
          }
        } catch (e) {
          console.error("Online details fetch failed", e);
        }
      } else {
        // Offline extraction
        if (details) {
          const candidates = [details?.data?.data, details?.data, details];
          for (const candidate of candidates) {
            if (
              candidate &&
              (candidate.journal_entry || candidate.journal_entries)
            ) {
              const val = candidate.journal_entry || candidate.journal_entries;
              entries = Array.isArray(val) ? val : val ? [val] : [];
              if (
                entries.length > 0 &&
                !details.journal_entry &&
                !details.journal_entries
              ) {
                details = candidate;
              }
              break;
            }
          }
        }
      }

      // Fallback details to header info
      details = details || bill;

      if (!details) {
        if (!isOnline()) {
          toast.error(t("progressBills.errors.offlineNoData"), { rtl: isRTL });
          setJournalEntriesModal({
            open: false,
            billId: null,
            entries: null,
            loading: false,
            billData: null,
          });
          return;
        }
      }

      setJournalEntriesModal({
        open: true,
        billId: bill.id,
        entries: entries || [],
        loading: false,
        billData: details,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        getReadableError(error, t("progressBills.errors.journalEntriesError")),
        { rtl: isRTL },
      );
      setJournalEntriesModal({
        open: false,
        billId: null,
        entries: null,
        loading: false,
        billData: null,
      });
    }
  };

  const handleShowProject = async bill => {
    try {
      let details = null;

      if (!isOnline()) {
        try {
          const allCached = await getAllProgressBillsCacheForQuery({
            per_page: 1000,
          });
          details = allCached.find(b => String(b.id) === String(bill.id));
        } catch (e) {}
      }

      if (!details && isOnline()) {
        try {
          const payload = await dispatch(
            getProgressBillDetails({ id: bill.id, language: i18n.language }),
          ).unwrap();
          details = payload?.data || payload;
        } catch (e) {
          console.error("Online details fetch failed", e);
        }
      }

      // Critical Fix: Prefer details.project, fallback to bill.project
      // This handles cases where 'details' is fetched but 'project' relation is missing,
      // but 'bill' (from list) still has it.
      const project = details?.project || bill?.project;

      if (project) {
        setProjectModal({ open: true, project: project });
      } else {
        toast.error(t("progressBills.errors.noProject"), { rtl: isRTL });
      }
    } catch (error) {
      // Final fallback
      if (bill.project) {
        setProjectModal({ open: true, project: bill.project });
      } else {
        toast.error(t("progressBills.errors.projectError"), { rtl: isRTL });
      }
    }
  };

  const handleShowContract = async bill => {
    try {
      let details = null;
      if (!isOnline()) {
        try {
          const allCached = await getAllProgressBillsCacheForQuery({
            per_page: 1000,
          });
          details = allCached.find(b => String(b.id) === String(bill.id));
        } catch (e) {}
      }

      if (!details && isOnline()) {
        try {
          const payload = await dispatch(
            getProgressBillDetails({ id: bill.id, language: i18n.language }),
          ).unwrap();
          details = payload?.data || payload;
        } catch (e) {
          console.error("Online details fetch failed", e);
        }
      }

      // Critical Fix: Prefer details.contract, fallback to bill.contract
      const contract = details?.contract || bill?.contract;

      if (contract) {
        setContractModal({ open: true, contract: contract });
      } else {
        toast.error(t("progressBills.errors.noContract"), { rtl: isRTL });
      }
    } catch (error) {
      if (bill.contract) {
        setContractModal({ open: true, contract: bill.contract });
      } else {
        toast.error(t("progressBills.errors.contractError"), { rtl: isRTL });
      }
    }
  };

  const handleShowAttachments = bill => {
    const attachments = Array.isArray(bill?.attachments)
      ? bill.attachments
      : [];

    if (attachments.length === 0) {
      toast.info(
        t("progressBills.attachmentsModal.noAttachments", {
          defaultValue: "لا توجد مرفقات",
        }),
        { rtl: isRTL },
      );
      return;
    }

    setAttachmentsModal({ open: true, attachments, billId: bill.id });
  };

  // Handle preview bill PDF
  const handlePreviewBill = async bill => {
    try {
      let details = null;
      if (!isOnline()) {
        try {
          const allCached = await getAllProgressBillsCacheForQuery({
            per_page: 1000,
          });
          details = allCached.find(b => String(b.id) === String(bill.id));
        } catch (e) {}
      }

      if (!details && isOnline()) {
        const payload = await dispatch(
          getProgressBillDetails({ id: bill.id, language: i18n.language }),
        ).unwrap();
        details = payload?.data || payload;
      }

      details = details || bill;

      if (details?.items) {
        details.items = details.items.map(item => ({
          ...item,
          total_qty: item.total_executed_qty ?? item.total_qty ?? 0,
          amount: item.total_amount ?? item.amount ?? 0,
          previous_qty: item.previous_qty ?? 0,
          current_qty: item.current_qty ?? item.total_executed_qty ?? 0,
          contract_item: {
            ...(item.contract_item || {}),
            id: item.contract_item?.id || item.id || item.contract_item_id,
            quantity: item.contract_item?.quantity || item.contract_qty || 0,
            unit_price: item.contract_item?.unit_price || item.unit_price || 0,
            description_ar:
              item.contract_item?.description_ar ||
              item.description ||
              item.description_ar,
            description_en:
              item.contract_item?.description_en ||
              item.description ||
              item.description_en,
          },
        }));
      }

      const token = getToken();
      if (token && details) {
        details = await mergeContractItems(
          details,
          token,
          getCurrentLanguage(),
        );
      }

      setBillDetailsForPrint(details);
      setBillForPrint(bill);
      setPreviewBill(bill);
      setIsPreviewOpen(true);
      setPreviewZoom(1.0);
      setPreviewPosition({ x: 0, y: 0 });
    } catch (error) {
      toast.error(
        getReadableError(error, t("progressBills.errors.fetchError")),
        { rtl: isRTL },
      );
    }
  };

  // Handle preview all bills
  const handlePreviewAll = async () => {
    try {
      const includeFilters = chooseExportScope("معاينة PDF للكل");
      const billsForPreview =
        await fetchAllProgressBillsForCurrentFilters(includeFilters);

      if (!billsForPreview || billsForPreview.length === 0) {
        toast.error(t("progressBills.noDataPreview"), { rtl: isRTL });
        return;
      }

      setExportProgressBills(billsForPreview);
      setPreviewBill({ all: true, bills: billsForPreview });
      setIsPreviewOpen(true);
      setPreviewZoom(1.0);
      setPreviewPosition({ x: 0, y: 0 });
    } catch (error) {
      console.error("Progress Bills preview all error:", error);
      toast.error(t("progressBills.noDataPreview"), { rtl: isRTL });
    }
  };

  // Handle mouse down for dragging
  const handleMouseDown = e => {
    if (e.button !== 0) return; // Only left mouse button
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - previewPosition.x,
      y: e.clientY - previewPosition.y,
    });
  };

  // Handle mouse move for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = e => {
      e.preventDefault();
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = e => {
      e.preventDefault();
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", handleMouseUp, { passive: false });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Handle reset to fit (original state with scroll)
  const handleResetPreview = () => {
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  // Handle print from preview
  const handlePrintFromPreview = async () => {
    if (!previewBill) return;
    if (previewBill.all) {
      await handlePrintAll(previewBill.bills);
    } else {
      // Save current preview state
      const currentBillDetails = billDetailsForPrint;
      const currentBill = billForPrint;

      await handlePrintBill(previewBill);

      // Restore preview state after printing
      if (currentBillDetails && currentBill) {
        setBillDetailsForPrint(currentBillDetails);
        setBillForPrint(currentBill);
      }
    }
  };

  const handlePrintBill = async bill => {
    try {
      let details = null;
      if (!isOnline()) {
        try {
          const allCached = await getAllProgressBillsCacheForQuery({
            per_page: 1000,
          });
          details = allCached.find(b => String(b.id) === String(bill.id));
        } catch (e) {}
      }

      if (!details && isOnline()) {
        const payload = await dispatch(
          getProgressBillDetails({ id: bill.id, language: i18n.language }),
        ).unwrap();
        details = payload?.data || payload;
      }

      details = details || bill;

      if (details?.items) {
        details.items = details.items.map(item => ({
          ...item,
          total_qty: item.total_executed_qty ?? item.total_qty ?? 0,
          amount: item.total_amount ?? item.amount ?? 0,
          previous_qty: item.previous_qty ?? 0,
          current_qty: item.current_qty ?? item.total_executed_qty ?? 0,
          contract_item: {
            ...(item.contract_item || {}),
            id: item.contract_item?.id || item.id || item.contract_item_id,
            quantity: item.contract_item?.quantity || item.contract_qty || 0,
            unit_price: item.contract_item?.unit_price || item.unit_price || 0,
            description_ar:
              item.contract_item?.description_ar ||
              item.description ||
              item.description_ar,
            description_en:
              item.contract_item?.description_en ||
              item.description ||
              item.description_en,
          },
        }));
      }

      const token = getToken();
      if (token && details) {
        details = await mergeContractItems(
          details,
          token,
          getCurrentLanguage(),
        );
      }

      setBillDetailsForPrint(details);
      setBillForPrint(bill);
      setPdfType("single");

      await new Promise(resolve => requestAnimationFrame(() => resolve()));

      // Wait for fonts to load
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));

      setIsGeneratingBill(true);

      const element =
        document.getElementById("progress-bill-preview-print") ||
        document.getElementById("progress-bill-pdf");
      if (!element) {
        toast.error(t("progressBills.errors.printError"), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById("progress-bill-pdf");
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction =
              i18n.language === "ar" ? "rtl" : "ltr";
            clonedElement.style.setProperty(
              "-webkit-font-smoothing",
              "antialiased",
            );
            clonedElement.style.setProperty(
              "-moz-osx-font-smoothing",
              "grayscale",
            );
            clonedElement.style.textRendering = "optimizeLegibility";

            const allElements = clonedElement.querySelectorAll("*");
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily =
                  '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = "bold";
                el.style.setProperty("-webkit-font-smoothing", "antialiased");
                el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                el.style.textRendering = "optimizeLegibility";
              }
            });
          }
        },
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("l", "mm", "a4"); // 'l' for landscape
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        pdfWidth,
        pdfHeight,
        undefined,
        "FAST",
      );

      pdf.save(
        `Progress_Bill_${bill.bill_no || bill.id}_${new Date().toISOString().split("T")[0]}.pdf`,
      );
    } catch (err) {
      console.error("Progress Bill PDF error:", err);
      toast.error(t("progressBills.errors.pdfError"), { rtl: isRTL });
    } finally {
      setIsGeneratingBill(false);
      setBillForPrint(null);
      setBillDetailsForPrint(null);
      setPdfType(null);
    }
  };

  // Handle print all progress bills
  const handlePrintAll = async (sourceBills = null) => {
    try {
      setIsGeneratingPdfAll(true);
      const includeFilters =
        Array.isArray(sourceBills) && sourceBills.length > 0
          ? true
          : chooseExportScope("طباعة PDF للكل");
      const billsForPrint =
        Array.isArray(sourceBills) && sourceBills.length > 0
          ? sourceBills
          : await fetchAllProgressBillsForCurrentFilters(includeFilters);

      if (!billsForPrint || billsForPrint.length === 0) {
        toast.error(t("progressBills.errors.noData"), { rtl: isRTL });
        return;
      }

      setExportProgressBills(billsForPrint);
      setPdfType("all");
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));

      const element = document.getElementById("progress-bills-pdf-all");
      if (!element) {
        toast.error(
          t("progressBills.errors.printError", {
            defaultValue: "قالب الطباعة غير متوفر",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const pageNodes = element.querySelectorAll(".progress-bills-print-page");
      if (!pageNodes.length) {
        toast.error(
          t("progressBills.errors.printError", {
            defaultValue: "قالب الطباعة غير متوفر",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const applyCloneFontStyles = clonedEl => {
        if (!clonedEl) return;
        clonedEl.style.fontFamily =
          '"Times New Roman Bold", "Times New Roman", "Times", serif';
        clonedEl.style.fontWeight = "bold";
        clonedEl.style.direction = isRTL ? "rtl" : "ltr";
        clonedEl.style.setProperty("-webkit-font-smoothing", "antialiased");
        clonedEl.style.setProperty("-moz-osx-font-smoothing", "grayscale");
        clonedEl.style.textRendering = "optimizeLegibility";
        clonedEl.querySelectorAll("*").forEach(el => {
          if (el.style) {
            el.style.setProperty("-webkit-font-smoothing", "antialiased");
            el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
            el.style.textRendering = "optimizeLegibility";
          }
        });
      };

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pageNodes.length; i++) {
        const pageEl = pageNodes[i];
        const canvas = await html2canvas(pageEl, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: true,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: clonedDoc => {
            const root = clonedDoc.body;
            applyCloneFontStyles(root);
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        let imgH = (canvas.height * pdfWidth) / canvas.width;
        if (imgH > pdfHeight) {
          imgH = pdfHeight;
        }
        if (i > 0) {
          pdf.addPage();
        }
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgH, undefined, "FAST");
      }

      pdf.save(`Progress_Bills_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Progress Bills PDF error:", error);
      toast.error(
        getReadableError(
          error,
          t("progressBills.errors.pdfError", {
            defaultValue: "حدث خطأ أثناء إنشاء ملف فواتير التقدم",
          }),
        ),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdfAll(false);
      setPdfType(null);
    }
  };

  // Handle export single progress bill to Excel
  const handleExportBillToExcel = async bill => {
    try {
      let details = null;
      if (!isOnline()) {
        try {
          const allCached = await getAllProgressBillsCacheForQuery({
            per_page: 1000,
          });
          details = allCached.find(b => String(b.id) === String(bill.id));
        } catch (e) {}
      }

      if (!details && isOnline()) {
        const payload = await dispatch(
          getProgressBillDetails({ id: bill.id, language: i18n.language }),
        ).unwrap();
        details = payload?.data || payload;
      }

      details = details || bill;

      if (details?.items) {
        details.items = details.items.map(item => ({
          ...item,
          total_qty: item.total_executed_qty ?? item.total_qty ?? 0,
          amount: item.total_amount ?? item.amount ?? 0,
          previous_qty: item.previous_qty ?? 0,
          current_qty: item.current_qty ?? item.total_executed_qty ?? 0,
          contract_item: {
            ...(item.contract_item || {}),
            id: item.contract_item?.id || item.id || item.contract_item_id,
            quantity: item.contract_item?.quantity || item.contract_qty || 0,
            unit_price: item.contract_item?.unit_price || item.unit_price || 0,
            description_ar:
              item.contract_item?.description_ar ||
              item.description ||
              item.description_ar,
            description_en:
              item.contract_item?.description_en ||
              item.description ||
              item.description_en,
          },
        }));
      }

      const contractName = formatContract(details.contract || bill.contract);
      const projectName = details.project
        ? isRTL
          ? details.project.name_ar || details.project.name_en
          : details.project.name_en || details.project.name_ar
        : "—";

      const data = [
        {
          [t("progressBills.table.billNo")]: bill.bill_no || bill.id || "—",
          [t("progressBills.fields.invoiceNumber", {
            defaultValue: "رقم الفاتورة",
          })]: bill.invoice_number || "—",
          [t("progressBills.contract")]: contractName,
          [t("progressBills.project")]: projectName,
          [t("progressBills.table.date")]: formatDate(bill.date) || "—",
          [t("progressBills.table.periodStart")]:
            formatDate(bill.period_start) || "—",
          [t("progressBills.table.periodEnd")]:
            formatDate(bill.period_end) || "—",
          [t("progressBills.table.totalAmount")]: formatCurrency(
            bill.total_amount,
          ),
          [t("progressBills.table.netAmount")]: formatCurrency(bill.net_amount),
          [t("progressBills.table.status")]: getStatusText(bill.status),
        },
      ];

      const wb = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(
        wb,
        summarySheet,
        isRTL
          ? t("progressBills.export.sheetName")
          : t("progressBills.export.sheetName", {
              defaultValue: "Progress Bill",
            }),
      );

      const itemRows = (Array.isArray(details.items) ? details.items : []).map(
        (item, index) => {
          const contractItem = item.contract_item || {};
          const contractQty = Number(contractItem.quantity || 0) || 0;
          const previousQty = getPreviouslyExecutedQtyFromItem(item);
          const currentQty = getCurrentQtyFromItem(item);
          const totalQty =
            Number(item.total_qty ?? previousQty + currentQty) || 0;
          const remainingQty =
            Number(item.remaining_qty ?? contractQty - totalQty) || 0;
          const unitPrice = Number(contractItem.unit_price || 0) || 0;

          return {
            [isRTL ? "م" : "#"]: index + 1,
            [t("progressBills.pdf.table1.itemNumber", {
              defaultValue: "Item No.",
            })]: item.id || contractItem.id || index + 1,
            [t("progressBills.pdf.table1.workDescription", {
              defaultValue: "Work Description",
            })]: isRTL
              ? contractItem.description_ar ||
                contractItem.description_en ||
                "—"
              : contractItem.description_en ||
                contractItem.description_ar ||
                "—",
            [t("progressBills.pdf.table1.executedQuantity", {
              defaultValue: "Executed Quantity",
            })]: currentQty,
            [t("progressBills.pdf.table1.contractQuantity", {
              defaultValue: "Contract Quantity",
            })]: contractQty,
            [t("progressBills.pdf.table1.previouslyExecuted", {
              defaultValue: "Previously Executed",
            })]: previousQty,
            [t("progressBills.pdf.table1.currentQuantity", {
              defaultValue: "Current Quantity",
            })]: currentQty,
            [t("progressBills.pdf.table1.remainingQuantity", {
              defaultValue: "Remaining Quantity",
            })]: remainingQty,
            [t("progressBills.pdf.table1.price", {
              defaultValue: "Price",
            })]: unitPrice,
            [t("progressBills.pdf.table1.total", {
              defaultValue: "Total",
            })]: Number(item.amount ?? totalQty * unitPrice) || 0,
          };
        },
      );

      if (itemRows.length > 0) {
        const itemsSheet = XLSX.utils.json_to_sheet(itemRows);
        XLSX.utils.book_append_sheet(
          wb,
          itemsSheet,
          isRTL ? "بنود المستخلص" : "Bill Items",
        );
      }
      const identifier = bill.bill_no || bill.id;
      const fileName = `${isRTL ? t("progressBills.export.fileName") : t("progressBills.export.fileName", { defaultValue: "ProgressBill" })}_${identifier}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("progressBills.export.success", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Export Excel error:", error);
      toast.error(
        getReadableError(
          error,
          t("progressBills.errors.exportError", {
            defaultValue: "حدث خطأ أثناء تصدير البيانات",
          }),
        ),
        { rtl: isRTL },
      );
    }
  };

  // Handle export all progress bills to Excel
  const handleExportAllToExcel = async () => {
    try {
      const includeFilters = chooseExportScope("تصدير Excel للكل");
      const billsForExport =
        await fetchAllProgressBillsForCurrentFilters(includeFilters);

      if (!billsForExport || billsForExport.length === 0) {
        toast.error(t("progressBills.errors.noData"), { rtl: isRTL });
        return;
      }

      const data = billsForExport.map((bill, index) => {
        const contractName = formatContract(bill.contract);
        const projectName = bill.project
          ? isRTL
            ? bill.project.name_ar || bill.project.name_en
            : bill.project.name_en || bill.project.name_ar
          : "—";

        return {
          م: (paginationInfo.current_page - 1) * perPage + (index + 1),
          [t("progressBills.table.billNo")]: bill.bill_no || bill.id || "—",
          [t("progressBills.fields.invoiceNumber", {
            defaultValue: "رقم الفاتورة",
          })]: bill.invoice_number || "—",
          [t("progressBills.contract")]: contractName,
          [t("progressBills.project")]: projectName,
          [t("progressBills.table.date")]: formatDate(bill.date) || "—",
          [t("progressBills.table.periodStart")]:
            formatDate(bill.period_start) || "—",
          [t("progressBills.table.periodEnd")]:
            formatDate(bill.period_end) || "—",
          [t("progressBills.table.totalAmount")]: formatCurrency(
            bill.total_amount,
          ),
          [t("progressBills.table.netAmount")]: formatCurrency(bill.net_amount),
          [t("progressBills.table.status")]: getStatusText(bill.status),
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        isRTL ? t("progressBills.export.sheetName") : "Progress Bills",
      );
      const scopeSuffix =
        includeFilters && hasActiveFilters ? "Filtered" : "All";
      const fileName = `${isRTL ? t("progressBills.export.fileName") : "ProgressBills"}_${scopeSuffix}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("progressBills.export.success", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Export all Excel error:", error);
      toast.error(
        getReadableError(
          error,
          t("progressBills.errors.exportError", {
            defaultValue: "حدث خطأ أثناء تصدير البيانات",
          }),
        ),
        { rtl: isRTL },
      );
    }
  };

  const formatDate = dateString => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = amount => {
    if (!amount) return "0.00";
    return parseFloat(amount).toFixed(2);
  };

  const renderStatus = status => {
    const statusMap = {
      draft: { text: t("progressBills.status.draft"), color: "#f39c12" },
      submitted: {
        text: t("progressBills.status.submitted"),
        color: "#3498db",
      },
      approved: { text: t("progressBills.status.approved"), color: "#27ae60" },
      rejected: { text: t("progressBills.status.rejected"), color: "#e74c3c" },
      paid: { text: t("progressBills.status.paid"), color: "#2ecc71" },
    };
    const statusInfo = statusMap[status] || {
      text: status || "—",
      color: "#999",
    };
    return (
      <span style={{ color: statusInfo.color, fontWeight: "bold" }}>
        {statusInfo.text}
      </span>
    );
  };

  const getStatusText = status => {
    const statusMap = {
      draft: t("progressBills.status.draft"),
      submitted: t("progressBills.status.submitted"),
      approved: t("progressBills.status.approved"),
      rejected: t("progressBills.status.rejected"),
      paid: t("progressBills.status.paid"),
    };
    return statusMap[status] || status || "—";
  };

  const formatContract = contract => {
    if (!contract) return "—";
    const code = contract.code || contract.contract_no || "";
    const projectName = contract.project
      ? ` - ${isRTL ? contract.project.name_ar || contract.project.name_en : contract.project.name_en || contract.project.name_ar}`
      : "";
    return `${code}${projectName}` || `#${contract.id}`;
  };

  const formatProject = project => {
    if (!project) return "—";
    const code = project.code ? `${project.code} - ` : "";
    const name = isRTL
      ? project.name_ar || project.name_en
      : project.name_en || project.name_ar;
    return `${code}${name}`.trim() || `#${project.id}`;
  };

  const formatCustomer = customer => {
    if (!customer) return "—";
    const code = customer.code ? `${customer.code} - ` : "";
    const name = customer.name_ar || customer.name_en || customer.name || "";
    return `${code}${name}`.trim() || `#${customer.id}`;
  };

  const getProjectNameFromBill = bill => {
    if (!bill) return "—";
    const project = bill.project;
    if (project) {
      return isRTL
        ? project.name_ar || project.name_en || "—"
        : project.name_en || project.name_ar || "—";
    }
    return "—";
  };

  const getCustomerNameFromBill = bill => {
    if (!bill) return "—";
    if (bill.customer_name) return bill.customer_name;
    if (bill.project?.customer) {
      return isRTL
        ? bill.project.customer.name_ar || bill.project.customer.name_en || "—"
        : bill.project.customer.name_en || bill.project.customer.name_ar || "—";
    }
    if (bill.contract?.customer) {
      return isRTL
        ? bill.contract.customer.name_ar ||
            bill.contract.customer.name_en ||
            "—"
        : bill.contract.customer.name_en ||
            bill.contract.customer.name_ar ||
            "—";
    }
    return "—";
  };

  return (
    <div
      style={{
        padding: 30,
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20 }}>
          {t("progressBills.title", {
            defaultValue: "Progress Bills",
          })}
        </h1>
        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <label
              style={{ color: "white", fontSize: 14, whiteSpace: "nowrap" }}
            >
              {t("common.perPage", { defaultValue: "Per Page" })}:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => {
                  const current = parseInt(perPageInput, 10) || 1;
                  const newValue = Math.max(1, current - 1);
                  setPerPageInput(String(newValue));
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "6px 0 0 6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max={pagination?.total || progressBills.length || 1000}
                value={perPageInput}
                onChange={e => {
                  setPerPageInput(e.target.value);
                }}
                style={{
                  width: "70px",
                  padding: "6px 10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              />
              <button
                onClick={() => {
                  const total = pagination?.total || progressBills.length || 0;
                  const maxValue = total > 0 ? total : 1000;
                  const current = parseInt(perPageInput, 10) || 1;
                  const newValue = Math.min(maxValue, current + 1);
                  setPerPageInput(String(newValue));
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={() => {
                const total = pagination?.total || progressBills.length || 0;
                const maxValue = total > 0 ? total : 1000;
                const numeric = parseInt(perPageInput, 10) || 1;
                const safeValue = Math.max(1, Math.min(maxValue, numeric));
                setPerPage(safeValue);
                setPerPageInput(String(safeValue));
                setCurrentPage(1);
              }}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.apply", { defaultValue: "تطبيق" })}
            </button>
          </div>
        </div>
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 12 }}
      >
        <div style={{ position: "relative", flex: "1 1 320px" }}>
          <FaSearch
            style={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#888",
            }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t("progressBills.searchPlaceholder")}
            style={{
              width: "100%",
              padding: isRTL ? "12px 42px 12px 16px" : "12px 16px 12px 42px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: 8,
              color: "white",
            }}
          />
        </div>
        <div
          style={{ position: "relative", flex: "1 1 320px" }}
          data-dropdown-container
        >
          <div
            data-dropdown-toggle
            onClick={() => setContractDropdownOpen(prev => !prev)}
            style={dropdownDisplayStyle}
          >
            <span>
              {contractId
                ? formatContract(
                    contractOptions.find(
                      c => String(c.id) === String(contractId),
                    ),
                  )
                : t("progressBills.selectContract")}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={contractDropdownOpen}
            searchTerm={contractSearchTerm}
            onSearchChange={setContractSearchTerm}
            searchInputRef={contractSearchInputRef}
            items={contractOptions}
            pagination={contractPagination}
            onPageChange={setContractPage}
            onClose={() => setContractDropdownOpen(false)}
            onSelect={item => {
              setContractId(String(item.id));
              setContractDropdownOpen(false);
              setContractSearchTerm("");
              setContractPage(1);
              setCurrentPage(1);
            }}
            t={t}
            label={t("progressBills.contract")}
            renderItem={formatContract}
          />
        </div>
        <div
          style={{ position: "relative", flex: "1 1 320px" }}
          data-dropdown-container
        >
          <div
            data-dropdown-toggle
            onClick={() => setProjectDropdownOpen(prev => !prev)}
            style={dropdownDisplayStyle}
          >
            <span>
              {projectId
                ? formatProject(
                    projectOptions.find(
                      p => String(p.id) === String(projectId),
                    ),
                  )
                : t("progressBills.project", { defaultValue: "المشروع" })}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={projectDropdownOpen}
            searchTerm={projectSearchTerm}
            onSearchChange={setProjectSearchTerm}
            searchInputRef={projectSearchInputRef}
            items={projectOptions}
            pagination={projectPagination}
            onPageChange={setProjectPage}
            onClose={() => setProjectDropdownOpen(false)}
            onSelect={item => {
              setProjectId(String(item.id));
              setProjectDropdownOpen(false);
              setProjectSearchTerm("");
              setProjectPage(1);
              setCurrentPage(1);
            }}
            t={t}
            label={t("progressBills.project", { defaultValue: "المشروع" })}
            renderItem={formatProject}
          />
        </div>
        <div
          style={{ position: "relative", flex: "1 1 320px" }}
          data-dropdown-container
        >
          <div
            data-dropdown-toggle
            onClick={() => setCustomerDropdownOpen(prev => !prev)}
            style={dropdownDisplayStyle}
          >
            <span>
              {customerId
                ? formatCustomer(
                    customerOptions.find(
                      c => String(c.id) === String(customerId),
                    ),
                  )
                : t("progressBills.customer", { defaultValue: "العميل" })}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={customerDropdownOpen}
            searchTerm={customerSearchTerm}
            onSearchChange={setCustomerSearchTerm}
            searchInputRef={customerSearchInputRef}
            items={customerOptions}
            pagination={customerPagination}
            onPageChange={setCustomerPage}
            onClose={() => setCustomerDropdownOpen(false)}
            onSelect={item => {
              setCustomerId(String(item.id));
              setCustomerDropdownOpen(false);
              setCustomerSearchTerm("");
              setCustomerPage(1);
              setCurrentPage(1);
            }}
            t={t}
            label={t("progressBills.customer", { defaultValue: "العميل" })}
            renderItem={formatCustomer}
          />
        </div>
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 16 }}
      >
        <div style={{ flex: "1 1 200px" }}>
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, width: "100%" }}
          >
            <option value="">{t("progressBills.allStatuses")}</option>
            <option value="draft">{t("progressBills.status.draft")}</option>
            <option value="submitted">
              {t("progressBills.status.submitted")}
            </option>
            <option value="approved">
              {t("progressBills.status.approved")}
            </option>
            <option value="rejected">
              {t("progressBills.status.rejected")}
            </option>
            <option value="paid">{t("progressBills.status.paid")}</option>
          </select>
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <select
            value={typeFilter}
            onChange={e => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, width: "100%" }}
          >
            <option value="">
              {t("progressBills.typeAll", { defaultValue: "كل النوع" })}
            </option>
            <option value="bill">
              {t("progressBills.typeBill", { defaultValue: "فاتورة" })}
            </option>
            <option value="claim">
              {t("progressBills.typeClaim", { defaultValue: "مطالبة" })}
            </option>
          </select>
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <select
            value={billCategoryFilter}
            onChange={e => {
              setBillCategoryFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, width: "100%" }}
          >
            <option value="">
              {t("progressBills.billCategoryAll", { defaultValue: "كل الفئة" })}
            </option>
            <option value="running">
              {t("progressBills.billCategoryRunning", { defaultValue: "جاري" })}
            </option>
            <option value="final">
              {t("progressBills.billCategoryFinal", { defaultValue: "ختامي" })}
            </option>
          </select>
        </div>
        <div style={{ flex: "1 1 160px" }}>
          <input
            type="date"
            value={dateFrom}
            onChange={e => {
              setDateFrom(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, width: "100%" }}
            placeholder={t("progressBills.dateFrom", {
              defaultValue: "من تاريخ",
            })}
          />
        </div>
        <div style={{ flex: "1 1 160px" }}>
          <input
            type="date"
            value={dateTo}
            onChange={e => {
              setDateTo(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, width: "100%" }}
            placeholder={t("progressBills.dateTo", {
              defaultValue: "إلى تاريخ",
            })}
          />
        </div>
      </div>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={handleReset}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            width: "auto",
          }}
        >
          {t("progressBills.reset")}
        </button>
        <button
          onClick={() => navigate("/progress-bills/add")}
          style={{
            backgroundColor: "#AC2000",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
          }}
        >
          <FaPlus /> {t("progressBills.newBill")}
        </button>
        <button
          onClick={handlePreviewAll}
          style={{
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
          }}
        >
          <FaEye />{" "}
          {t("progressBills.preview.all", { defaultValue: "عرض PDF للكل" })}
        </button>
        <button
          onClick={handlePrintAll}
          disabled={isGeneratingPdfAll}
          style={{
            backgroundColor: isGeneratingPdfAll ? "#666" : "#17a2b8",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: isGeneratingPdfAll ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
            opacity: isGeneratingPdfAll ? 0.6 : 1,
          }}
        >
          <FaPrint />{" "}
          {isGeneratingPdfAll
            ? t("progressBills.print.generating")
            : t("progressBills.print.all")}
        </button>
        <button
          onClick={handleExportAllToExcel}
          style={{
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
          }}
        >
          <FaFileExcel />{" "}
          {t("progressBills.export.all", { defaultValue: "تصدير Excel الكل" })}
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "auto",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{
                    padding: 14,
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: 14,
                    borderBottom: "1px solid #333",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    width: 60,
                  }}
                  onClick={() => handleSort("id")}
                >
                  #{getSortIcon("id")}
                </th>
                {[
                  {
                    key: "bill_no",
                    label: t("progressBills.tableHeaders.billNo"),
                  },
                  {
                    key: "invoice_number",
                    label: t("progressBills.fields.invoiceNumber", {
                      defaultValue: "رقم الفاتورة",
                    }),
                  },
                  {
                    key: null,
                    label: t("progressBills.tableHeaders.contractDetails", {
                      defaultValue: "تفاصيل العقد",
                    }),
                  },
                  {
                    key: null,
                    label: t("progressBills.tableHeaders.project", {
                      defaultValue: "المشروع",
                    }),
                  },
                  {
                    key: null,
                    label: t("progressBills.customer", {
                      defaultValue: "العميل",
                    }),
                  },
                  {
                    key: "status",
                    label: t("progressBills.tableHeaders.status"),
                  },
                  {
                    key: null,
                    label: t("progressBills.tableHeaders.projectDetails", {
                      defaultValue: "تفاصيل المشروع",
                    }),
                  },
                  { key: "date", label: t("progressBills.tableHeaders.date") },
                  {
                    key: "period_start",
                    label: t("progressBills.tableHeaders.from"),
                  },
                  {
                    key: "period_end",
                    label: t("progressBills.tableHeaders.to"),
                  },
                  {
                    key: "total_amount",
                    label: t("progressBills.tableHeaders.totalAmount"),
                  },
                  {
                    key: "net_amount",
                    label: t("progressBills.tableHeaders.netAmount"),
                  },
                  {
                    key: null,
                    label: t("progressBills.tableHeaders.journalEntries"),
                  },
                  { key: null, label: t("progressBills.tableHeaders.actions") },
                ].map(col => (
                  <th
                    key={col.label}
                    onClick={col.key ? () => handleSort(col.key) : undefined}
                    style={{
                      padding: 14,
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 14,
                      borderBottom: "1px solid #333",
                      whiteSpace: "nowrap",
                      cursor: col.key ? "pointer" : "default",
                    }}
                  >
                    {col.label}
                    {col.key ? getSortIcon(col.key) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={15}
                    style={{ textAlign: "center", padding: 20, color: "#ccc" }}
                  >
                    {t("progressBills.loading")}
                  </td>
                </tr>
              ) : progressBills.length === 0 ? (
                <tr>
                  <td
                    colSpan={15}
                    style={{ textAlign: "center", padding: 20, color: "#ccc" }}
                  >
                    {t("progressBills.noData")}
                  </td>
                </tr>
              ) : (
                progressBills.map((bill, index) => (
                  <tr
                    key={bill.id || index}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td style={cellStyle}>
                      {(paginationInfo.current_page - 1) * perPage +
                        (index + 1)}
                    </td>
                    <td style={cellStyle}>
                      <div style={{ fontWeight: "bold" }}>
                        {bill.bill_no || "—"}
                      </div>
                    </td>
                    <td style={cellStyle}>
                      <div>{bill.invoice_number || "—"}</div>
                    </td>
                    <td style={cellStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        {bill.contract && (
                          <ActionButton
                            title={t("progressBills.viewContractDetails")}
                            bg="#6c757d"
                            onClick={() => handleShowContract(bill)}
                            style={{ padding: "6px 12px", fontSize: 13 }}
                          >
                            {/* {t('progressBills.viewDetails')} */}
                            <FaEye />
                          </ActionButton>
                        )}
                        {!bill.contract && <span>—</span>}
                      </div>
                    </td>
                    <td style={cellStyle}>{getProjectNameFromBill(bill)}</td>
                    <td style={cellStyle}>{getCustomerNameFromBill(bill)}</td>
                    <td style={cellStyle}>{renderStatus(bill.status)}</td>
                    <td style={cellStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        {bill.project && (
                          <ActionButton
                            title={t("progressBills.viewProjectDetails")}
                            bg="#6c757d"
                            onClick={() => handleShowProject(bill)}
                            style={{ padding: "6px 12px", fontSize: 13 }}
                          >
                            {/* {t('progressBills.viewDetails')} */}
                            <FaEye />
                          </ActionButton>
                        )}
                        {!bill.project && <span>—</span>}
                      </div>
                    </td>
                    <td style={cellStyle}>{formatDate(bill.date)}</td>
                    <td style={cellStyle}>{formatDate(bill.period_start)}</td>
                    <td style={cellStyle}>{formatDate(bill.period_end)}</td>
                    {(() => {
                      const summary = Array.isArray(bill.invoice_summary)
                        ? bill.invoice_summary
                        : [];
                      const getSummaryVal = key => {
                        const row = summary.find(r => r.key === key);
                        return row ? parseFloat(row.value) : null;
                      };
                      const sTotal = getSummaryVal("total_amount");
                      const sNet = getSummaryVal("net_amount");
                      let totalAmt =
                        sTotal !== null ? sTotal : bill.total_amount;
                      let netAmt = sNet !== null ? sNet : bill.net_amount;

                      // Fallback: If invoice_summary is missing and backend is sending mathematically broken cumulative totals
                      // We calculate the true current bill total: Net - Tax + Retention + Advance Deduction
                      if (
                        summary.length === 0 &&
                        bill.net_amount &&
                        bill.tax_amount
                      ) {
                        const tax = parseFloat(bill.tax_amount) || 0;
                        const net = parseFloat(bill.net_amount) || 0;
                        const ret = parseFloat(bill.retention_amount) || 0;
                        const adv =
                          parseFloat(bill.advance_payment_deduction) || 0;
                        const dedTax =
                          parseFloat(bill.deduction_tax_amount) || 0;

                        const calculatedTotal = net - tax + ret + adv + dedTax;
                        // If calculated total is valid and significantly different from backend's wrong total
                        if (
                          calculatedTotal >= 0 &&
                          Math.abs(
                            calculatedTotal -
                              parseFloat(bill.total_amount || 0),
                          ) > 1
                        ) {
                          totalAmt = calculatedTotal;
                        }
                      }

                      return (
                        <>
                          <td style={cellStyle}>{formatCurrency(totalAmt)}</td>
                          <td style={cellStyle}>{formatCurrency(netAmt)}</td>
                        </>
                      );
                    })()}
                    <td style={cellStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        <ActionButton
                          title={t("progressBills.journalEntries")}
                          bg="#17a2b8"
                          onClick={() => handleShowJournalEntries(bill)}
                          style={{ padding: "6px 12px", fontSize: 13 }}
                        >
                          {/* {t('progressBills.viewDetails')} */}
                          <FaEye />
                        </ActionButton>
                      </div>
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 8,
                          flexWrap: "nowrap",
                        }}
                      >
                        <ActionButton
                          title={t("progressBills.print.single")}
                          onClick={() => handlePrintBill(bill)}
                          disabled={isGeneratingBill}
                        >
                          <FaPrint />
                        </ActionButton>
                        <ActionButton
                          title={t("progressBills.export.single")}
                          bg="#28a745"
                          onClick={() => handleExportBillToExcel(bill)}
                        >
                          <FaFileExcel />
                        </ActionButton>
                        {bill.status !== "approved" &&
                          bill.status !== "paid" && (
                            <ActionButton
                              title={t("progressBills.approve")}
                              bg="#28a745"
                              onClick={() => {
                                setBillToApprove(bill);
                                setApproveModalOpen(true);
                              }}
                              disabled={approvingBillId === bill.id}
                            >
                              <FaCheck />
                            </ActionButton>
                          )}
                        <ActionButton
                          title={t("progressBills.view")}
                          onClick={() => {
                            if (!bill || !bill.id) {
                              toast.error(
                                t("progressBills.errors.fetchError"),
                                { rtl: isRTL },
                              );
                              return;
                            }
                            handlePreviewBill(bill);
                          }}
                        >
                          <FaEye />
                        </ActionButton>
                        {bill?.attachments &&
                          Array.isArray(bill.attachments) &&
                          bill.attachments.length > 0 && (
                            <ActionButton
                              title={t("progressBills.attachments", {
                                defaultValue: "المرفقات",
                              })}
                              bg="#9b59b6"
                              onClick={() => handleShowAttachments(bill)}
                            >
                              <FaPaperclip />
                            </ActionButton>
                          )}
                        <ActionButton
                          title={t("progressBills.edit")}
                          onClick={() => {
                            if (!bill || !bill.id) {
                              toast.error(
                                t("progressBills.errors.fetchError"),
                                { rtl: isRTL },
                              );
                              return;
                            }
                            navigate(`/progress-bills/edit/${bill.id}`);
                          }}
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t("progressBills.delete")}
                          bg="#c0392b"
                          onClick={() => {
                            setBillToDelete(bill);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && pagination.last_page && pagination.last_page >= 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            padding: 16,
            borderTop: "1px solid #333",
          }}
        >
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={paginationInfo.current_page === 1}
            style={paginationButtonStyle(paginationInfo.current_page === 1)}
          >
            {t("progressBills.pagination.previous")}
          </button>
          <span style={{ padding: 8 }}>
            {paginationInfo.current_page} / {paginationInfo.last_page}
          </span>
          <button
            onClick={() =>
              setCurrentPage(prev =>
                prev >= paginationInfo.last_page
                  ? paginationInfo.last_page
                  : prev + 1,
              )
            }
            disabled={paginationInfo.current_page >= paginationInfo.last_page}
            style={paginationButtonStyle(
              paginationInfo.current_page >= paginationInfo.last_page,
            )}
          >
            {t("progressBills.pagination.next")}
          </button>
        </div>
      )}

      {deleteModalOpen && (
        <div
          style={modalOverlayStyle}
          onClick={() => setDeleteModalOpen(false)}
        >
          <div style={modalCardStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 16, color: "white" }}>
              {t("progressBills.deleteModal.title")}
            </h3>
            <p style={{ color: "#ccc", marginBottom: 24 }}>
              {t("progressBills.deleteModal.message")}{" "}
              {billToDelete?.bill_no || `#${billToDelete?.id}`}?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setDeleteModalOpen(false)}
                style={modalSecondaryButtonStyle}
              >
                {t("progressBills.deleteModal.cancel")}
              </button>
              <button onClick={handleDelete} style={modalPrimaryButtonStyle}>
                {t("progressBills.deleteModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {approveModalOpen && (
        <div
          style={modalOverlayStyle}
          onClick={() => setApproveModalOpen(false)}
        >
          <div
            style={{ ...modalCardStyle, direction: dir, textAlign }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 16, color: "white" }}>
              {t("progressBills.approveModal.title")}
            </h3>
            <p style={{ color: "#ccc", marginBottom: 24 }}>
              {t("progressBills.approveModal.message")}{" "}
              {billToApprove?.bill_no || `#${billToApprove?.id}`}?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => {
                  setApproveModalOpen(false);
                  setBillToApprove(null);
                }}
                style={modalSecondaryButtonStyle}
              >
                {t("progressBills.approveModal.cancel")}
              </button>
              <button
                onClick={handleApprove}
                style={modalPrimaryButtonStyle}
                disabled={approvingBillId === billToApprove?.id}
              >
                {approvingBillId === billToApprove?.id
                  ? t("progressBills.approveModal.processing")
                  : t("progressBills.approveModal.approve")}
              </button>
            </div>
          </div>
        </div>
      )}

      {journalEntriesModal.open &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() =>
              setJournalEntriesModal({
                open: false,
                billId: null,
                entries: null,
                loading: false,
                billData: null,
              })
            }
          >
            <div
              style={{
                ...modalCardStyle,
                maxWidth: 900,
                maxHeight: "90vh",
                overflow: "auto",
                margin: "auto",
                direction: dir,
                textAlign,
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: 16, color: "white" }}>
                {t("progressBills.journalEntriesModal.title")}
              </h3>
              {journalEntriesModal.loading ? (
                <div
                  style={{ textAlign: "center", padding: 40, color: "#ccc" }}
                >
                  {t("progressBills.journalEntriesModal.loading")}
                </div>
              ) : journalEntriesModal.entries &&
                Array.isArray(journalEntriesModal.entries) &&
                journalEntriesModal.entries.length > 0 ? (
                journalEntriesModal.entries.map((entry, entryIndex) => (
                  <div
                    key={entry.id || entryIndex}
                    style={{
                      marginBottom:
                        entryIndex < journalEntriesModal.entries.length - 1
                          ? 24
                          : 0,
                    }}
                  >
                    {/* Journal Entry Info */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(250px, 1fr))",
                        gap: 16,
                        marginBottom: 24,
                        padding: 16,
                        backgroundColor: "#1a1f2e",
                        borderRadius: 8,
                        border: "1px solid #333",
                      }}
                    >
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.entryNo",
                        )}
                        value={entry.id || "—"}
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.entryDate",
                        )}
                        value={formatDate(entry.entry_date)}
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.description",
                        )}
                        value={entry.description || "—"}
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.reference",
                        )}
                        value={entry.reference || "—"}
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.status",
                        )}
                        value={
                          entry.status === "posted"
                            ? t(
                                "progressBills.journalEntriesModal.statusValues.posted",
                              )
                            : entry.status === "draft"
                              ? t(
                                  "progressBills.journalEntriesModal.statusValues.draft",
                                )
                              : entry.status || "—"
                        }
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.reversed",
                        )}
                        value={
                          entry.is_reversed
                            ? t(
                                "progressBills.journalEntriesModal.reversedValues.yes",
                              )
                            : t(
                                "progressBills.journalEntriesModal.reversedValues.no",
                              )
                        }
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.entryType",
                        )}
                        value={entry.entryable_type || "—"}
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.entryId",
                        )}
                        value={entry.entryable_id || "—"}
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.createdAt",
                        )}
                        value={formatDate(entry.created_at)}
                      />
                      <DetailItem
                        label={t(
                          "progressBills.journalEntriesModal.fields.updatedAt",
                        )}
                        value={formatDate(entry.updated_at)}
                      />
                      {entry.accepted_at && (
                        <DetailItem
                          label={t(
                            "progressBills.journalEntriesModal.fields.acceptedAt",
                          )}
                          value={formatDate(entry.accepted_at)}
                        />
                      )}
                      {entry.posted_at && (
                        <DetailItem
                          label={t(
                            "progressBills.journalEntriesModal.fields.postedAt",
                          )}
                          value={formatDate(entry.posted_at)}
                        />
                      )}
                      {entry.reverses_entry_id && (
                        <DetailItem
                          label={t(
                            "progressBills.journalEntriesModal.fields.reversesEntry",
                          )}
                          value={entry.reverses_entry_id}
                        />
                      )}
                    </div>

                    {/* Journal Entry Details */}
                    {entry.details &&
                      Array.isArray(entry.details) &&
                      entry.details.length > 0 && (
                        <div
                          style={{
                            backgroundColor: "#1a1f2e",
                            border: "1px solid #333",
                            borderRadius: 12,
                            overflow: "hidden",
                          }}
                        >
                          <h4
                            style={{
                              color: "white",
                              marginBottom: 12,
                              padding: 12,
                              backgroundColor: "#2a2a2a",
                              fontSize: 14,
                            }}
                          >
                            {t(
                              "progressBills.journalEntriesModal.detailsTitle",
                            )}
                          </h4>
                          <div style={{ overflowX: "auto" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                minWidth: 800,
                              }}
                            >
                              <thead>
                                <tr style={{ backgroundColor: "#AC2000" }}>
                                  {[
                                    t(
                                      "progressBills.journalEntriesModal.detailsHeaders.account",
                                    ),
                                    t(
                                      "progressBills.journalEntriesModal.detailsHeaders.code",
                                    ),
                                    t(
                                      "progressBills.journalEntriesModal.detailsHeaders.description",
                                    ),
                                    t(
                                      "progressBills.journalEntriesModal.detailsHeaders.debit",
                                    ),
                                    t(
                                      "progressBills.journalEntriesModal.detailsHeaders.credit",
                                    ),
                                  ].map(header => (
                                    <th
                                      key={header}
                                      style={{
                                        padding: 14,
                                        textAlign: "center",
                                        color: "white",
                                        fontWeight: "bold",
                                        fontSize: 14,
                                        borderBottom: "1px solid #333",
                                      }}
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {entry.details.map((detail, idx) => (
                                  <tr
                                    key={detail.id || idx}
                                    style={{ borderBottom: "1px solid #333" }}
                                  >
                                    <td style={cellStyle}>
                                      {detail.account?.name_ar ||
                                        detail.account?.name_en ||
                                        "—"}
                                    </td>
                                    <td style={cellStyle}>
                                      {detail.account?.code || "—"}
                                    </td>
                                    <td style={cellStyle}>
                                      {detail.line_description || "—"}
                                    </td>
                                    <td style={cellStyle}>
                                      {formatCurrency(detail.debit)}
                                    </td>
                                    <td style={cellStyle}>
                                      {formatCurrency(detail.credit)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                  </div>
                ))
              ) : (
                <div
                  style={{ textAlign: "center", padding: 40, color: "#ccc" }}
                >
                  {t("progressBills.journalEntriesModal.noEntries")}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  marginTop: 24,
                }}
              >
                <button
                  onClick={() =>
                    setJournalEntriesModal({
                      open: false,
                      billId: null,
                      entries: null,
                      loading: false,
                    })
                  }
                  style={modalSecondaryButtonStyle}
                >
                  {t("progressBills.journalEntriesModal.close")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {projectModal.open &&
        projectModal.project &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => setProjectModal({ open: false, project: null })}
          >
            <div
              style={{
                ...modalCardStyle,
                maxWidth: 800,
                maxHeight: "90vh",
                overflow: "auto",
                margin: "auto",
                direction: dir,
                textAlign,
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: 16, color: "white" }}>
                {t("progressBills.projectModal.title")}
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: 16,
                  marginBottom: 24,
                }}
              >
                <DetailItem
                  label={t("progressBills.projectModal.fields.code")}
                  value={projectModal.project.code || "—"}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.nameAr")}
                  value={projectModal.project.name_ar || "—"}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.nameEn")}
                  value={projectModal.project.name_en || "—"}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.customer")}
                  value={
                    projectModal.project.customer?.name_ar ||
                    projectModal.project.customer?.name ||
                    "—"
                  }
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.startDate")}
                  value={formatDate(projectModal.project.start_date)}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.endDate")}
                  value={formatDate(projectModal.project.end_date)}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.contractValue")}
                  value={formatCurrency(projectModal.project.contract_value)}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.status")}
                  value={projectModal.project.status || "—"}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.location")}
                  value={projectModal.project.location || "—"}
                />
                <DetailItem
                  label={t("progressBills.projectModal.fields.notes")}
                  value={projectModal.project.notes || "—"}
                />
              </div>
              {projectModal.project.attachments &&
                Array.isArray(projectModal.project.attachments) &&
                projectModal.project.attachments.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h4
                      style={{ color: "white", marginBottom: 12, fontSize: 14 }}
                    >
                      {t("progressBills.projectModal.attachments")}
                    </h4>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {projectModal.project.attachments.map(
                        (attachment, idx) => (
                          <div
                            key={attachment.id || idx}
                            style={{
                              padding: "8px 12px",
                              backgroundColor: "#1a1f2e",
                              border: "1px solid #333",
                              borderRadius: 6,
                              fontSize: 12,
                              color: "#ccc",
                            }}
                          >
                            {attachment.file_name ||
                              attachment.name ||
                              `${t("progressBills.projectModal.attachment")} ${idx + 1}`}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  marginTop: 24,
                }}
              >
                <button
                  onClick={() =>
                    setProjectModal({ open: false, project: null })
                  }
                  style={modalSecondaryButtonStyle}
                >
                  {t("progressBills.projectModal.close")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {contractModal.open &&
        contractModal.contract &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => setContractModal({ open: false, contract: null })}
          >
            <div
              style={{
                ...modalCardStyle,
                maxWidth: 800,
                maxHeight: "90vh",
                overflow: "auto",
                margin: "auto",
                direction: dir,
                textAlign,
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: 16, color: "white" }}>
                {t("progressBills.contractModal.title")}
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: 16,
                  marginBottom: 24,
                }}
              >
                <DetailItem
                  label={t("progressBills.contractModal.fields.code")}
                  value={contractModal.contract.code || "—"}
                />
                <DetailItem
                  label={t("progressBills.contractModal.fields.project")}
                  value={
                    contractModal.contract.project?.name_ar ||
                    contractModal.contract.project?.name_en ||
                    "—"
                  }
                />
                <DetailItem
                  label={t("progressBills.contractModal.fields.contractor")}
                  value={
                    contractModal.contract.customer?.name_ar ||
                    contractModal.contract.customer?.name ||
                    "—"
                  }
                />
                <DetailItem
                  label={t("progressBills.contractModal.fields.quotation")}
                  value={contractModal.contract.quotation?.quotation_no || "—"}
                />
                <DetailItem
                  label={t("progressBills.tableHeaders.date")}
                  value={formatDate(contractModal.contract.date)}
                />
                <DetailItem
                  label={t("progressBills.contractModal.fields.startDate")}
                  value={formatDate(contractModal.contract.start_date)}
                />
                <DetailItem
                  label={t("progressBills.contractModal.fields.endDate")}
                  value={formatDate(contractModal.contract.end_date)}
                />
                <DetailItem
                  label={t("progressBills.tableHeaders.totalAmount")}
                  value={formatCurrency(contractModal.contract.total_amount)}
                />
                <DetailItem
                  label={t(
                    "progressBills.contractModal.fields.retentionPercent",
                  )}
                  value={
                    contractModal.contract.retention_percent
                      ? `${contractModal.contract.retention_percent}%`
                      : "—"
                  }
                />
                <DetailItem
                  label={t(
                    "progressBills.contractModal.fields.advancePaymentPercent",
                  )}
                  value={
                    contractModal.contract.advance_payment_percent
                      ? `${contractModal.contract.advance_payment_percent}%`
                      : "—"
                  }
                />
                <DetailItem
                  label={t("progressBills.contractModal.fields.status")}
                  value={contractModal.contract.status || "—"}
                />
                <DetailItem
                  label={t("progressBills.contractModal.fields.notes")}
                  value={contractModal.contract.notes || "—"}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  marginTop: 24,
                }}
              >
                <button
                  onClick={() =>
                    setContractModal({ open: false, contract: null })
                  }
                  style={modalSecondaryButtonStyle}
                >
                  {t("progressBills.contractModal.close")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {attachmentsModal.open &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() =>
              setAttachmentsModal({
                open: false,
                attachments: [],
                billId: null,
              })
            }
          >
            <div
              style={{
                ...modalCardStyle,
                maxWidth: 900,
                maxHeight: "90vh",
                overflow: "auto",
                margin: "auto",
                direction: dir,
                textAlign,
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: 16, color: "white" }}>
                {t("progressBills.attachmentsModal.title", {
                  defaultValue: "المرفقات",
                })}
              </h3>

              {attachmentsModal.attachments &&
              Array.isArray(attachmentsModal.attachments) &&
              attachmentsModal.attachments.length > 0 ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: 16,
                  }}
                >
                  {attachmentsModal.attachments.map((attachment, idx) => {
                    const isImage = attachment.mime_type?.startsWith("image/");
                    const fileUrl =
                      attachment?.file_url ||
                      attachment?.url ||
                      attachment?.media_url ||
                      (attachment?.path
                        ? `${BASE_URL.slice(0, -3)}/storage/${attachment.path}`
                        : "") ||
                      attachment?.full_url ||
                      attachment?.link ||
                      "";

                    return (
                      <div
                        key={attachment.id || idx}
                        style={{
                          backgroundColor: "#1a1f2e",
                          border: "1px solid #333",
                          borderRadius: 8,
                          padding: 12,
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {isImage && fileUrl ? (
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              paddingTop: "75%",
                              backgroundColor: "#14263b",
                              borderRadius: 4,
                              overflow: "hidden",
                            }}
                          >
                            <img
                              // src={fileUrl}
                              src={`${BASE_URL.slice(0, -3)}/storage/${attachment.path}`}
                              alt={
                                attachment.file_name || `Attachment ${idx + 1}`
                              }
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                cursor: "pointer",
                              }}
                              onClick={() => window.open(fileUrl, "_blank")}
                              onError={e => {
                                e.target.style.display = "none";
                                const errorDiv = e.target.nextSibling;
                                if (errorDiv) errorDiv.style.display = "flex";
                              }}
                            />
                            <div
                              style={{
                                display: "none",
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#888",
                                fontSize: 12,
                              }}
                            >
                              {t("progressBills.attachmentsModal.imageError", {
                                defaultValue: "خطأ في تحميل الصورة",
                              })}
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              paddingTop: "75%",
                              backgroundColor: "#14263b",
                              borderRadius: 4,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#888",
                            }}
                          >
                            <FaFileAlt style={{ fontSize: 32 }} />
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 12,
                            color: "#ccc",
                            wordBreak: "break-word",
                            textAlign: "center",
                          }}
                        >
                          {attachment.file_name ||
                            attachment.name ||
                            `Attachment ${idx + 1}`}
                        </div>
                        {fileUrl && (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              backgroundColor: "#AC2000",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: 6,
                              cursor: "pointer",
                              textAlign: "center",
                              textDecoration: "none",
                              fontSize: 12,
                              fontWeight: "bold",
                            }}
                          >
                            {isImage
                              ? t("progressBills.attachmentsModal.view", {
                                  defaultValue: "عرض",
                                })
                              : t("progressBills.attachmentsModal.download", {
                                  defaultValue: "تحميل",
                                })}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{ textAlign: "center", padding: 40, color: "#ccc" }}
                >
                  {t("progressBills.attachmentsModal.noAttachments", {
                    defaultValue: "لا توجد مرفقات",
                  })}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  marginTop: 24,
                }}
              >
                <button
                  onClick={() =>
                    setAttachmentsModal({
                      open: false,
                      attachments: [],
                      billId: null,
                    })
                  }
                  style={modalSecondaryButtonStyle}
                >
                  {t("progressBills.attachmentsModal.close", {
                    defaultValue: "إغلاق",
                  })}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {/* PDF Preview Modal */}
      {isPreviewOpen &&
        previewBill &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 999999,
              display: "flex",
              flexDirection: "column",
              padding: "20px",
              overflow: "hidden",
            }}
            onClick={e => {
              if (e.target === e.currentTarget) {
                setIsPreviewOpen(false);
                setPreviewBill(null);
              }
            }}
            onWheel={e => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setPreviewZoom(prev =>
                  Math.max(0.3, Math.min(2, prev + delta)),
                );
              }
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1f2e",
                borderRadius: "12px",
                padding: "20px",
                width: "100%",
                height: "100%",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexShrink: 0,
                }}
              >
                <h2 style={{ color: "white", margin: 0, fontSize: "20px" }}>
                  {previewBill?.all
                    ? t("progressBills.preview.all", {
                        defaultValue: "معاينة جميع فواتير الأعمال",
                      })
                    : t("progressBills.preview.title", {
                        defaultValue: "معاينة فاتورة الأعمال",
                      })}
                </h2>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      backgroundColor: "#2a2f3e",
                      padding: "6px 12px",
                      borderRadius: 6,
                    }}
                  >
                    <button
                      onClick={() =>
                        setPreviewZoom(prev => Math.max(0.3, prev - 0.1))
                      }
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                      title={t("progressBills.preview.zoomOut", {
                        defaultValue: "تصغير",
                      })}
                    >
                      −
                    </button>
                    <span
                      style={{
                        color: "white",
                        minWidth: "60px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {Math.round(previewZoom * 100)}%
                    </span>
                    <button
                      onClick={() =>
                        setPreviewZoom(prev => Math.min(2, prev + 0.1))
                      }
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                      title={t("progressBills.preview.zoomIn", {
                        defaultValue: "تكبير",
                      })}
                    >
                      +
                    </button>
                    <button
                      onClick={handleResetPreview}
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                      title={t("progressBills.preview.fitToScreen", {
                        defaultValue: "ملاءمة الشاشة",
                      })}
                    >
                      {t("progressBills.preview.fit", {
                        defaultValue: "ملاءمة",
                      })}
                    </button>
                  </div>
                  <button
                    onClick={handlePrintFromPreview}
                    disabled={isGeneratingBill || isGeneratingPdfAll}
                    style={{
                      backgroundColor:
                        isGeneratingBill || isGeneratingPdfAll
                          ? "#666"
                          : "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 20px",
                      cursor:
                        isGeneratingBill || isGeneratingPdfAll
                          ? "not-allowed"
                          : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: isGeneratingBill || isGeneratingPdfAll ? 0.6 : 1,
                    }}
                  >
                    <FaPrint />{" "}
                    {previewBill?.all
                      ? t("progressBills.print.all")
                      : t("progressBills.print.single")}
                  </button>
                  <button
                    onClick={() => {
                      setIsPreviewOpen(false);
                      setPreviewBill(null);
                    }}
                    style={{
                      backgroundColor: "#666",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 20px",
                      cursor: "pointer",
                    }}
                  >
                    {t("common.close")}
                  </button>
                </div>
              </div>
              <div
                ref={previewContainerRef}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  padding: "20px",
                  overflow: previewZoom <= 1.0 ? "auto" : "hidden",
                  flex: 1,
                  display: "flex",
                  alignItems: previewZoom <= 1.0 ? "flex-start" : "center",
                  justifyContent: previewZoom <= 1.0 ? "flex-start" : "center",
                  minHeight: 0,
                  position: "relative",
                  cursor:
                    previewZoom > 1.0 && isDragging
                      ? "grabbing"
                      : previewZoom > 1.0
                        ? "grab"
                        : "default",
                }}
                onMouseDown={previewZoom > 1.0 ? handleMouseDown : undefined}
              >
                <div
                  style={{
                    transform:
                      previewZoom > 1.0
                        ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                        : `scale(${previewZoom})`,
                    transformOrigin:
                      previewZoom <= 1.0 ? "top center" : "center center",
                    transition: isDragging ? "none" : "transform 0.2s ease",
                    width: previewZoom <= 1.0 ? "100%" : "1123px",
                    height: previewBill?.all ? "auto" : "794px",
                    userSelect: previewZoom > 1.0 ? "none" : "auto",
                    marginLeft: previewZoom <= 1.0 ? "0" : "0",
                  }}
                  onDragStart={e => e.preventDefault()}
                >
                  {previewBill?.all ? (
                    <AllProgressBillsPreviewTemplate
                      progressBills={previewBill.bills || progressBills}
                      isRTL={isRTL}
                      t={t}
                      filterText={activeFilterLabels.join(" | ")}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                      formatContract={formatContract}
                      getStatusText={getStatusText}
                      publicUrl={publicUrl}
                      headerLogo={headerLogo}
                    />
                  ) : (
                    <ProgressBillPreviewTemplate
                      bill={previewBill}
                      billDetails={billDetailsForPrint}
                      isRTL={isRTL}
                      i18n={i18n}
                      t={t}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                      formatContract={formatContract}
                      getStatusText={getStatusText}
                      convertNumberToArabicWords={convertNumberToArabicWords}
                      publicUrl={publicUrl}
                      headerLogo={headerLogo}
                      backgroundLogo={backgroundLogo}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Single-bill print source uses the same preview template */}
      {billForPrint && billDetailsForPrint && (
        <div
          id="progress-bill-preview-print"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "1123px",
            pointerEvents: "none",
          }}
        >
          <ProgressBillPreviewTemplate
            bill={billForPrint}
            billDetails={billDetailsForPrint}
            isRTL={isRTL}
            i18n={i18n}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            formatContract={formatContract}
            getStatusText={getStatusText}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            backgroundLogo={backgroundLogo}
          />
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }
          #progress-bill-pdf {
            position: relative !important;
            left: 0 !important;
            top: 0 !important;
            page-break-after: always;
            page-break-inside: avoid;
            display: flex !important;
            flex-direction: column !important;
            min-height: 100vh !important;
          }
          .print-footer {
            margin-top: auto !important;
            flex-shrink: 0 !important;
            position: relative !important;
            z-index: 1000 !important;
          }
          #progress-bill-pdf > div {
            display: flex !important;
            flex-direction: column !important;
            min-height: 100% !important;
          }
          .progress-bills-print-page {
            break-after: page;
            page-break-after: always;
            page-break-inside: avoid;
          }
          .progress-bills-print-page:last-of-type {
            break-after: auto;
            page-break-after: auto;
          }
          .all-progress-bills-print-table thead {
            display: table-header-group;
          }
          .all-progress-bills-print-table tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      {/* Hidden PDF Template */}
      {billForPrint &&
        billDetailsForPrint &&
        (() => {
          const summary = Array.isArray(billDetailsForPrint.invoice_summary)
            ? billDetailsForPrint.invoice_summary
            : [];
          const isRunning = billDetailsForPrint.bill_category
            ? billDetailsForPrint.bill_category === "running"
            : summary.length > 0
              ? !summary.some(
                  r => r.key === "previous_payments" && parseFloat(r.value) > 0,
                )
              : billDetailsForPrint.type !== "bill"; // claim without summary = running-like
          const isBill = billDetailsForPrint.type === "bill";
          const totalCells = isBill ? 14 : isRunning ? 12 : 11;
          const fmtQty = n => {
            const num = parseFloat(n) || 0;
            return Number.isInteger(num) ? String(num) : num.toFixed(2);
          };
          const getSummaryValue = key => {
            const row = summary.find(r => r.key === key);
            return parseFloat(row ? row.value : 0);
          };
          const netAmount = getSummaryValue("net_amount");

          return (
            <div
              id="progress-bill-pdf"
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                top: 0,
                width: "1123px",
                minHeight: "794px",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "10px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  height: "100%",
                  minHeight: "794px",
                  padding: "18px 18px 0",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: i18n.language === "ar" ? "rtl" : "ltr",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Header */}
                  <img
                    src={`${publicUrl}/topHeader.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    {/* Title */}
                    <div
                      style={{
                        marginTop: "20px",
                        textAlign: "center",
                        direction: i18n.language === "ar" ? "rtl" : "ltr",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#000080",
                          marginBottom: "8px",
                        }}
                      >
                        {t("progressBills.pdf.title")}{" "}
                        {billDetailsForPrint.bill_no ||
                          billForPrint.bill_no ||
                          `#${billForPrint.id}`}
                      </div>
                    </div>

                    {/* Info Section - صفوف منفصلة بدلاً من جدول */}
                    <div
                      style={{
                        marginTop: "12px",
                        direction: i18n.language === "ar" ? "rtl" : "ltr",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "12px",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* Right Side - التاريخ، رقم الفاتورة، اسم العميل، اسم المشروع، ملاحظات */}
                      <div
                        style={{
                          flex: "1 1 45%",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {billDetailsForPrint.date && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "700",
                                fontSize: "14px",
                                color: "#000080",
                                minWidth: "100px",
                              }}
                            >
                              {t("progressBills.pdf.date")}:
                            </span>
                            <span
                              style={{
                                fontWeight: "700",
                                fontSize: "15px",
                                color: "#000080",
                              }}
                            >
                              {formatDate(billDetailsForPrint.date)}
                            </span>
                          </div>
                        )}
                        {billDetailsForPrint.invoice_number && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "700",
                                fontSize: "14px",
                                color: "#000080",
                                minWidth: "100px",
                              }}
                            >
                              {t("progressBills.pdf.invoiceNumber", {
                                defaultValue: "رقم الفاتورة",
                              })}
                              :
                            </span>
                            <span
                              style={{
                                fontWeight: "500",
                                fontSize: "15px",
                                color: "#000",
                              }}
                            >
                              {billDetailsForPrint.invoice_number}
                            </span>
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: "700",
                              fontSize: "14px",
                              color: "#000080",
                              minWidth: "100px",
                            }}
                          >
                            {t("progressBills.pdf.billType", {
                              defaultValue: "نوع المستخلص",
                            })}
                            :
                          </span>
                        </div>
                        {(billDetailsForPrint.contract?.customer?.name_ar ||
                          billDetailsForPrint.contract?.customer?.name_en ||
                          billDetailsForPrint.project?.customer?.name_ar ||
                          billDetailsForPrint.project?.customer?.name_en) && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "700",
                                fontSize: "14px",
                                color: "#000080",
                                minWidth: "100px",
                              }}
                            >
                              {t("progressBills.pdf.clientName")}:
                            </span>
                            <span
                              style={{
                                fontWeight: "500",
                                fontSize: "15px",
                                color: "#000",
                              }}
                            >
                              {billDetailsForPrint.contract?.customer
                                ?.name_ar ||
                                billDetailsForPrint.contract?.customer
                                  ?.name_en ||
                                billDetailsForPrint.project?.customer
                                  ?.name_ar ||
                                billDetailsForPrint.project?.customer?.name_en}
                            </span>
                          </div>
                        )}
                        {(billDetailsForPrint.project?.name_ar ||
                          billDetailsForPrint.project?.name_en) && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "700",
                                fontSize: "14px",
                                color: "#000080",
                                minWidth: "100px",
                              }}
                            >
                              {t("progressBills.pdf.projectName")}:
                            </span>
                            <span
                              style={{
                                fontWeight: "500",
                                fontSize: "15px",
                                color: "#000",
                              }}
                            >
                              {billDetailsForPrint.project?.name_ar ||
                                billDetailsForPrint.project?.name_en}
                            </span>
                          </div>
                        )}
                        {billDetailsForPrint.notes && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "700",
                                fontSize: "14px",
                                color: "#000080",
                                minWidth: "100px",
                              }}
                            >
                              {t("progressBills.pdf.notes")}:
                            </span>
                            <span
                              style={{
                                fontWeight: "500",
                                fontSize: "15px",
                                color: "#000080",
                              }}
                            >
                              {billDetailsForPrint.notes}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Left Side - نوع المستخلص، فئة المستخلص، خلال الفترة من، إلى */}
                      <div
                        style={{
                          flex: "1 1 45%",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: "700",
                              fontSize: "14px",
                              color: "#000080",
                            }}
                          >
                            {t("progressBills.pdf.billType")}:
                          </span>
                          <span
                            style={{
                              fontWeight: "500",
                              fontSize: "15px",
                              color: "#000080",
                            }}
                          >
                            {billDetailsForPrint.type === "bill"
                              ? i18n.language === "ar"
                                ? "فاتورة"
                                : "Bill"
                              : billDetailsForPrint.type === "claim"
                                ? i18n.language === "ar"
                                  ? "مطالبة"
                                  : "Claim"
                                : "—"}
                          </span>
                        </div>
                        {/* Bill Category */}
                        {(billDetailsForPrint.type ||
                          billDetailsForPrint.bill_category) && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "700",
                                fontSize: "14px",
                                color: "#000080",
                              }}
                            >
                              {t("progressBills.pdf.billCategory", {
                                defaultValue: "فئة المستخلص",
                              })}
                              :
                            </span>
                            <span
                              style={{
                                fontWeight: "500",
                                fontSize: "15px",
                                color: "#000080",
                              }}
                            >
                              {billDetailsForPrint.bill_category_label ||
                                (billDetailsForPrint.bill_category ===
                                "running"
                                  ? i18n.language === "ar"
                                    ? "جاري"
                                    : "Running"
                                  : billDetailsForPrint.bill_category ===
                                      "final"
                                    ? i18n.language === "ar"
                                      ? "ختامي"
                                      : "Final"
                                    : isRunning
                                      ? i18n.language === "ar"
                                        ? "جاري"
                                        : "Running"
                                      : i18n.language === "ar"
                                        ? "ختامي"
                                        : "Final")}
                            </span>
                          </div>
                        )}
                        {billDetailsForPrint.period_start &&
                          billDetailsForPrint.period_end && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "700",
                                  fontSize: "14px",
                                  color: "#000080",
                                }}
                              >
                                {t("progressBills.pdf.periodFrom")}:
                              </span>
                              <span
                                style={{
                                  fontWeight: "700",
                                  fontSize: "15px",
                                  color: "#000080",
                                }}
                              >
                                {formatDate(billDetailsForPrint.period_start)}
                              </span>
                              <span
                                style={{
                                  fontWeight: "700",
                                  fontSize: "14px",
                                  color: "#000080",
                                }}
                              >
                                {t("progressBills.pdf.periodTo")}:
                              </span>
                              <span
                                style={{
                                  fontWeight: "700",
                                  fontSize: "15px",
                                  color: "#000080",
                                }}
                              >
                                {formatDate(billDetailsForPrint.period_end)}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Background Logo - فوق الجدول */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${headerLogo})`,
                        backgroundSize: "95%",
                        backgroundPosition: "center center",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.05,
                        zIndex: 3,
                        pointerEvents: "none",
                      }}
                    />

                    {/* Items Table and Financial Summary Side by Side */}
                    {billDetailsForPrint.items &&
                      billDetailsForPrint.items.length > 0 && (
                        <div
                          style={{
                            marginTop: "18px",
                            direction: i18n.language === "ar" ? "rtl" : "ltr",
                            display: "flex",
                            gap: "12px",
                            alignItems: "flex-start",
                            position: "relative",
                            zIndex: 2,
                          }}
                        >
                          {/* Items Table - على اليسار */}
                          <div style={{ flex: "1 1 60%" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: "12px",
                                border: "1px solid #000",
                                borderSpacing: 0,
                                margin: 0,
                                padding: 0,
                                cellSpacing: 0,
                                cellPadding: 0,
                                position: "relative",
                                zIndex: 2,
                                backgroundColor: "#ffffff",
                              }}
                            >
                              <thead>
                                <tr style={{ backgroundColor: "#ffffff" }}>
                                  <th
                                    style={{
                                      border: "1px solid #000",
                                      borderBottom: "1px solid #000",
                                      padding: "4px 6px",
                                      textAlign: "center",
                                      fontWeight: "700",
                                      margin: 0,
                                      borderCollapse: "collapse",
                                      fontSize: "12px",
                                      lineHeight: "1.3",
                                      width: "5%",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    {t("progressBills.pdf.table1.itemNumber", {
                                      defaultValue: "رقم البند",
                                    })}
                                  </th>
                                  <th
                                    colSpan="4"
                                    style={{
                                      border: "1px solid #000",
                                      borderBottom: "1px solid #000",
                                      padding: "4px 6px",
                                      textAlign: "center",
                                      fontWeight: "700",
                                      margin: 0,
                                      borderCollapse: "collapse",
                                      fontSize: "12px",
                                      lineHeight: "1.3",
                                    }}
                                  >
                                    {t(
                                      "progressBills.pdf.table1.workDescription",
                                      { defaultValue: "بيان الاعمال" },
                                    )}
                                  </th>
                                  {isBill ? (
                                    <>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.executedQuantity",
                                          { defaultValue: "الكمية المنفذة" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.contractQuantity",
                                          { defaultValue: "الكمية بالعقد" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.previouslyExecuted",
                                          { defaultValue: "ما سبق تنفيذه" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.currentQuantity",
                                          { defaultValue: "الكمية الحالية" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.totalExecuted",
                                          {
                                            defaultValue: "إجمالي المنفذ",
                                          },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t("progressBills.percentage", {
                                          defaultValue: "النسبة %",
                                        })}
                                      </th>
                                    </>
                                  ) : isRunning ? (
                                    <>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.executedQuantity",
                                          { defaultValue: "الكمية المنفذة" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.contractQuantity",
                                          { defaultValue: "الكمية بالعقد" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.previouslyExecuted",
                                          { defaultValue: "ما سبق تنفيذه" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.remainingQuantity",
                                          { defaultValue: "الكمية المتبقية" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t("progressBills.percentage", {
                                          defaultValue: "نسبة الاتمام",
                                        })}
                                      </th>
                                    </>
                                  ) : (
                                    <>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.executedQuantity",
                                          { defaultValue: "الكمية المنفذة" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.contractQuantity",
                                          { defaultValue: "الكمية بالعقد" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.previouslyExecuted",
                                          { defaultValue: "ما سبق تنفيذه" },
                                        )}
                                      </th>
                                      <th
                                        style={{
                                          border: "1px solid #000",
                                          borderBottom: "1px solid #000",
                                          padding: "4px 6px",
                                          textAlign: "center",
                                          fontWeight: "700",
                                          margin: 0,
                                          borderCollapse: "collapse",
                                          fontSize: "12px",
                                          lineHeight: "1.3",
                                          width: "8%",
                                        }}
                                      >
                                        {t(
                                          "progressBills.pdf.table1.currentQuantity",
                                          { defaultValue: "الكمية الحالية" },
                                        )}
                                      </th>
                                    </>
                                  )}
                                  <th
                                    style={{
                                      border: "1px solid #000",
                                      borderBottom: "1px solid #000",
                                      padding: "4px 6px",
                                      textAlign: "center",
                                      fontWeight: "700",
                                      margin: 0,
                                      borderCollapse: "collapse",
                                      fontSize: "12px",
                                      lineHeight: "1.3",
                                      width: "8%",
                                    }}
                                  >
                                    {t("progressBills.pdf.table1.price", {
                                      defaultValue: "السعر",
                                    })}
                                  </th>
                                  {isBill && (
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        padding: "4px 6px",
                                        textAlign: "center",
                                        fontWeight: "700",
                                        margin: 0,
                                        borderCollapse: "collapse",
                                        fontSize: "12px",
                                        lineHeight: "1.3",
                                        width: "8%",
                                      }}
                                    >
                                      {t(
                                        "progressBills.pdf.table1.contractTotal",
                                        { defaultValue: "الإجمالي التعاقدي" },
                                      )}
                                    </th>
                                  )}
                                  <th
                                    style={{
                                      border: "1px solid #000",
                                      borderBottom: "1px solid #000",
                                      padding: "4px 6px",
                                      textAlign: "center",
                                      fontWeight: "700",
                                      margin: 0,
                                      borderCollapse: "collapse",
                                      fontSize: "12px",
                                      lineHeight: "1.3",
                                      width: "8%",
                                    }}
                                  >
                                    {t("progressBills.pdf.table1.total", {
                                      defaultValue: "الاجمالي",
                                    })}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {billDetailsForPrint.items.map((item, idx) => {
                                  const contractItem = item.contract_item || {};
                                  const cQty = parseFloat(
                                    contractItem.quantity || 0,
                                  );
                                  const pQty =
                                    getPreviouslyExecutedQtyFromItem(item);
                                  const curQty = getCurrentQtyFromItem(item);
                                  const tQty = parseFloat(item.total_qty || 0);
                                  const price = parseFloat(
                                    contractItem.unit_price || 0,
                                  );
                                  const amt = parseFloat(item.amount || 0);
                                  const rQty = cQty - tQty;
                                  const pct =
                                    cQty > 0
                                      ? Math.round((tQty / cQty) * 100)
                                      : 0;

                                  const rowTdCell = {
                                    border: "1px solid #000",
                                    padding: "4px 6px",
                                    textAlign: "center",
                                    margin: 0,
                                    borderCollapse: "collapse",
                                    fontSize: "12px",
                                    lineHeight: "1.3",
                                    backgroundColor: "#ffffff",
                                  };
                                  return (
                                    <tr
                                      key={item.id || idx}
                                      style={{ backgroundColor: "#ffffff" }}
                                    >
                                      <td style={rowTdCell}>{idx + 1}</td>
                                      <td
                                        colSpan="4"
                                        style={{
                                          ...rowTdCell,
                                          direction:
                                            i18n.language === "ar"
                                              ? "rtl"
                                              : "ltr",
                                          textAlign: "center",
                                        }}
                                      >
                                        {i18n.language === "ar"
                                          ? contractItem.description_ar ||
                                            contractItem.description_en ||
                                            "—"
                                          : contractItem.description_en ||
                                            contractItem.description_ar ||
                                            "—"}
                                      </td>
                                      {isBill ? (
                                        <>
                                          <td style={rowTdCell}>
                                            {fmtQty(curQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(cQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(pQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(curQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(tQty)}
                                          </td>
                                          <td style={rowTdCell}>{pct}%</td>
                                          <td style={rowTdCell}>
                                            {formatCurrency(price)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {formatCurrency(
                                              parseFloat(
                                                item.contract_item
                                                  ?.total_price || cQty * price,
                                              ),
                                            )}
                                          </td>
                                          <td style={rowTdCell}>
                                            {formatCurrency(amt)}
                                          </td>
                                        </>
                                      ) : isRunning ? (
                                        <>
                                          <td style={rowTdCell}>
                                            {fmtQty(curQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(cQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(pQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(rQty)}
                                          </td>
                                          <td style={rowTdCell}>{pct}%</td>
                                          <td style={rowTdCell}>
                                            {formatCurrency(price)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {formatCurrency(amt)}
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          <td style={rowTdCell}>
                                            {fmtQty(curQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(cQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(pQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {fmtQty(curQty)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {formatCurrency(price)}
                                          </td>
                                          <td style={rowTdCell}>
                                            {formatCurrency(amt)}
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  );
                                })}
                                {/* Financial Summary */}
                                {summary.map((row, si) => {
                                  const rowTd2 = {
                                    border: "1px solid #000",
                                    padding: "4px 6px",
                                    textAlign: "center",
                                    direction:
                                      i18n.language === "ar" ? "rtl" : "ltr",
                                    fontWeight: "700",
                                    margin: 0,
                                    borderCollapse: "collapse",
                                    fontSize: "12px",
                                    lineHeight: "1.3",
                                    backgroundColor: "#ffffff",
                                  };
                                  const rowTd3 = {
                                    border: "1px solid #000",
                                    padding: "4px 6px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    margin: 0,
                                    borderCollapse: "collapse",
                                    fontSize: "12px",
                                    lineHeight: "1.3",
                                    backgroundColor: "#ffffff",
                                  };
                                  return (
                                    <tr
                                      key={row.key || si}
                                      style={{ backgroundColor: "#ffffff" }}
                                    >
                                      <td
                                        colSpan={
                                          isBill
                                            ? totalCells - 1
                                            : totalCells - 2
                                        }
                                        style={rowTd2}
                                      >
                                        {row.label}
                                      </td>
                                      <td style={rowTd3}>
                                        {formatCurrency(row.value)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                    {/* Bill Value Text - Above Signatures */}
                    <div
                      style={{
                        marginTop: "30px",
                        marginBottom: "20px",
                        direction: i18n.language === "ar" ? "rtl" : "ltr",
                        textAlign: "center",
                        fontSize: "13px",
                        lineHeight: 1.8,
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      <div style={{ fontWeight: "700", color: "#000080" }}>
                        {i18n.language === "ar"
                          ? "قيمة المستخلص فقط وقدره: "
                          : "Bill value only: "}
                        {convertNumberToArabicWords(
                          parseFloat(netAmount || 0),
                          t,
                        )}{" "}
                        {i18n.language === "ar"
                          ? t("progressBills.currency.egp")
                          : "EGP"}
                      </div>
                    </div>

                    {/* Signatures Section */}
                    <div
                      style={{
                        marginTop: "20px",
                        marginBottom: "20px",
                        direction: i18n.language === "ar" ? "rtl" : "ltr",
                        fontSize: "13px",
                        lineHeight: 1.8,
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "20px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            minWidth: "200px",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              color: "#000080",
                              fontWeight: "700",
                              fontSize: "13px",
                              marginBottom: "8px",
                            }}
                          >
                            {i18n.language === "ar"
                              ? "توقيع المحاسب"
                              : "Accountant Signature"}
                          </div>
                          <div
                            style={{
                              paddingTop: "5px",
                              fontSize: "10px",
                              letterSpacing: "1px",
                              color: "#000",
                            }}
                          >
                            ........................................
                          </div>
                        </div>

                        <div
                          style={{
                            flex: 1,
                            minWidth: "200px",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              color: "#000080",
                              fontWeight: "700",
                              fontSize: "13px",
                              marginBottom: "8px",
                            }}
                          >
                            {i18n.language === "ar"
                              ? "توقيع المراجعة"
                              : "Review Signature"}
                          </div>
                          <div
                            style={{
                              paddingTop: "5px",
                              fontSize: "10px",
                              letterSpacing: "1px",
                              color: "#000",
                            }}
                          >
                            ........................................
                          </div>
                        </div>

                        <div
                          style={{
                            flex: 1,
                            minWidth: "200px",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              color: "#000080",
                              fontWeight: "700",
                              fontSize: "13px",
                              marginBottom: "8px",
                            }}
                          >
                            {i18n.language === "ar"
                              ? "توقيع الاعتماد"
                              : "Approval Signature"}
                          </div>
                          <div
                            style={{
                              paddingTop: "5px",
                              fontSize: "10px",
                              letterSpacing: "1px",
                              color: "#000",
                            }}
                          >
                            ........................................
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div
                  className="print-footer"
                  style={{
                    marginTop: "auto",
                    flexShrink: 0,
                    position: "relative",
                    zIndex: 2,
                    alignSelf: "stretch",
                  }}
                >
                  <div
                    style={{
                      borderTop: "2px solid #000",
                      padding: "10px",
                      direction: i18n.language === "ar" ? "rtl" : "ltr",
                      fontSize: "13px",
                      backgroundColor: "#ffffff",
                      marginBottom: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* تاريخ الطباعة والوقت - على الشمال */}
                      <div
                        style={{
                          direction: i18n.language === "ar" ? "ltr" : "ltr",
                          fontSize: "12px",
                          color: "#000080",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div
                          style={{
                            textAlign: i18n.language === "ar" ? "left" : "left",
                          }}
                        >
                          {t("progressBills.pdf.footer.printDate")}:{" "}
                          {formatDate(new Date())}
                        </div>
                        <div
                          style={{
                            textAlign:
                              i18n.language === "ar" ? "center" : "left",
                          }}
                        >
                          {t("progressBills.pdf.footer.time")}:{" "}
                          {new Date().toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                      {/* المقر الرئيسي - متسنتر في النص */}
                      <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontWeight: "700", color: "#000080" }}>
                          {t("progressBills.pdf.footer.headquarters")}
                        </div>
                        <div style={{ color: "#000080" }}>
                          {t("progressBills.pdf.footer.address")}
                        </div>
                        <div style={{ color: "#000080", fontWeight: "700" }}>
                          {t("progressBills.pdf.footer.contact")}
                        </div>
                      </div>
                      {/* User Name - على اليمين */}
                      <div
                        style={{
                          textAlign: i18n.language === "ar" ? "right" : "right",
                          direction: i18n.language === "ar" ? "rtl" : "ltr",
                          fontSize: "12px",
                          color: "#000080",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div>
                          {t("progressBills.pdf.footer.userName")}:{" "}
                          {billDetailsForPrint.creator?.name || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Print Template for All Progress Bills */}
      {pdfType === "all" && (
        <AllProgressBillsPrintTemplate
          progressBills={
            exportProgressBills.length ? exportProgressBills : progressBills
          }
          isRTL={isRTL}
          t={t}
          filterText={activeFilterLabels.join(" | ")}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          formatContract={formatContract}
          getStatusText={getStatusText}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
        />
      )}
    </div>
  );
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onClose,
  onSelect,
  label,
  renderItem,
  t,
}) => {
  if (!isOpen) return null;

  const formatEntity =
    renderItem ||
    (entity => {
      if (!entity) return "—";
      const code = entity.code ? `${entity.code} - ` : "";
      const name = entity.name_ar || entity.name_en || "";
      return `${code}${name}` || `#${entity.id}`;
    });

  return (
    <div style={dropdownMenuStyle}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={t("progressBills.searchPlaceholderDropdown", { label })}
          style={dropdownSearchStyle}
          onClick={e => e.stopPropagation()}
        />
      </div>
      {pagination &&
        pagination.last_page !== undefined &&
        pagination.last_page !== null &&
        pagination.last_page >= 1 && (
          <div
            style={{
              padding: "8px",
              borderTop: "1px solid #333",
              background: "#14263b",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <span style={{ color: "#ccc", fontSize: 11 }}>
                {t("progressBills.pagination.page")}{" "}
                {pagination.current_page || 1}{" "}
                {t("progressBills.pagination.of")} {pagination.last_page || 1}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if ((pagination.current_page || 1) > 1) onPageChange(1);
                }}
                disabled={(pagination.current_page || 1) === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    (pagination.current_page || 1) === 1
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    (pagination.current_page || 1) === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    (pagination.current_page || 1) === 1
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ««
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if ((pagination.current_page || 1) > 1)
                    onPageChange((pagination.current_page || 1) - 1);
                }}
                disabled={(pagination.current_page || 1) === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    (pagination.current_page || 1) === 1
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    (pagination.current_page || 1) === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    (pagination.current_page || 1) === 1
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ‹
              </button>
              {(() => {
                const pages = [];
                const current = pagination.current_page || 1;
                const last = pagination.last_page || 1;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1)
                  start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(page => (
                  <button
                    key={page}
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      onPageChange(page);
                    }}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: page === current ? "#AC2000" : "#1a1f2e",
                      color: "white",
                      border:
                        page === current
                          ? "1px solid #AC2000"
                          : "1px solid #444",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: 10,
                      fontWeight: page === current ? "bold" : "normal",
                      minWidth: "28px",
                    }}
                  >
                    {page}
                  </button>
                ));
              })()}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (
                    (pagination.current_page || 1) < (pagination.last_page || 1)
                  )
                    onPageChange((pagination.current_page || 1) + 1);
                }}
                disabled={
                  (pagination.current_page || 1) === (pagination.last_page || 1)
                }
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    (pagination.current_page || 1) ===
                    (pagination.last_page || 1)
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    (pagination.current_page || 1) ===
                    (pagination.last_page || 1)
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    (pagination.current_page || 1) ===
                    (pagination.last_page || 1)
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ›
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (
                    (pagination.current_page || 1) < (pagination.last_page || 1)
                  )
                    onPageChange(pagination.last_page || 1);
                }}
                disabled={
                  (pagination.current_page || 1) === (pagination.last_page || 1)
                }
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    (pagination.current_page || 1) ===
                    (pagination.last_page || 1)
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    (pagination.current_page || 1) ===
                    (pagination.last_page || 1)
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    (pagination.current_page || 1) ===
                    (pagination.last_page || 1)
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                »»
              </button>
            </div>
          </div>
        )}
      <div style={{ maxHeight: 300, overflowY: "auto", paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={dropdownEmptyStyle}>{t("progressBills.noResults")}</div>
        ) : (
          (Array.isArray(items) ? items : []).map(item => (
            <div
              key={item.id}
              style={{
                padding: "10px 12px",
                borderTop: "1px solid #333",
                cursor: "pointer",
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatEntity(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const cellStyle = {
  padding: 14,
  textAlign: "center",
  color: "#ddd",
  fontSize: 13,
  borderBottom: "1px solid #333",
  whiteSpace: "nowrap",
};

const DetailItem = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{label}</div>
    <div
      style={{
        fontSize: 14,
        color: "white",
        wordWrap: "break-word",
        whiteSpace: "normal",
      }}
    >
      {value}
    </div>
  </div>
);

const filterInputStyle = {
  padding: 12,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  color: "white",
  fontSize: 14,
};

const paginationButtonStyle = disabled => ({
  padding: "8px 16px",
  backgroundColor: disabled ? "#333" : "#AC2000",
  color: "white",
  border: "none",
  borderRadius: 6,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const ActionButton = ({
  children,
  onClick,
  title,
  bg = "#2f3644",
  disabled = false,
}) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    title={title}
    disabled={disabled}
    style={{
      backgroundColor: disabled ? "#6c757d" : bg,
      color: "white",
      border: "none",
      borderRadius: 6,
      padding: "8px 10px",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 38,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {children}
  </button>
);

const dropdownDisplayStyle = {
  width: "100%",
  padding: "12px 14px",
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  color: "white",
  textAlign: "right",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
};

const dropdownSearchStyle = {
  width: "100%",
  padding: 10,
  borderRadius: 6,
  border: "1px solid #333",
  backgroundColor: "#14263b",
  color: "white",
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: "center",
  color: "#ccc",
  fontSize: 13,
};

const dropdownPaginationStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 10,
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 20001,
  padding: 16,
  overflow: "auto",
};

const modalCardStyle = {
  backgroundColor: "#1b3350",
  borderRadius: 12,
  padding: 24,
  border: "1px solid #333",
  width: "100%",
  position: "relative",
  zIndex: 20002,
  maxWidth: 420,
  margin: "auto",
};

const modalPrimaryButtonStyle = {
  backgroundColor: "#AC2000",
  color: "white",
  border: "none",
  padding: "12px 20px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 14,
  fontWeight: "bold",
};

const modalSecondaryButtonStyle = {
  backgroundColor: "#666",
  color: "white",
  border: "none",
  padding: "12px 20px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 14,
};

// Helper function to convert number to Arabic words
const convertNumberToArabicWords = (num, tFunc) => {
  const numStr = parseFloat(num).toFixed(2);
  const [integerPart, decimalPart] = numStr.split(".");
  let intNum = parseInt(integerPart, 10);

  const ones = [
    "",
    "واحد",
    "اثنان",
    "ثلاثة",
    "أربعة",
    "خمسة",
    "ستة",
    "سبعة",
    "ثمانية",
    "تسعة",
  ];
  const tens = [
    "",
    "",
    "عشرون",
    "ثلاثون",
    "أربعون",
    "خمسون",
    "ستون",
    "سبعون",
    "ثمانون",
    "تسعون",
  ];
  const teens = [
    "عشرة",
    "أحد عشر",
    "اثنا عشر",
    "ثلاثة عشر",
    "أربعة عشر",
    "خمسة عشر",
    "ستة عشر",
    "سبعة عشر",
    "ثمانية عشر",
    "تسعة عشر",
  ];
  const hundreds = [
    "",
    "مائة",
    "مائتان",
    "ثلاثمائة",
    "أربعمائة",
    "خمسمائة",
    "ستمائة",
    "سبعمائة",
    "ثمانمائة",
    "تسعمائة",
  ];

  const convertThreeDigits = n => {
    if (n === 0) return "";
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const one = n % 10;
      if (one === 0) return tens[ten];
      return `${ones[one]} و${tens[ten]}`;
    }
    const hundred = Math.floor(n / 100);
    const remainder = n % 100;
    if (remainder === 0) return hundreds[hundred];
    return `${hundreds[hundred]} و${convertThreeDigits(remainder)}`;
  };

  if (intNum === 0) return tFunc("progressBills.numberWords.zero");

  let result = "";
  if (intNum >= 1000000) {
    const millions = Math.floor(intNum / 1000000);
    result +=
      convertThreeDigits(millions) +
      " " +
      tFunc("progressBills.numberWords.million");
    const remainder = intNum % 1000000;
    if (remainder > 0)
      result += " " + tFunc("progressBills.numberWords.and") + " ";
    intNum = remainder;
  }
  if (intNum >= 1000) {
    const thousands = Math.floor(intNum / 1000);
    if (thousands === 1) {
      result += tFunc("progressBills.numberWords.thousand");
    } else if (thousands === 2) {
      result += tFunc("progressBills.numberWords.twoThousands");
    } else if (thousands >= 3 && thousands <= 10) {
      result +=
        convertThreeDigits(thousands) +
        " " +
        tFunc("progressBills.numberWords.thousands");
    } else {
      result +=
        convertThreeDigits(thousands) +
        " " +
        tFunc("progressBills.numberWords.thousand");
    }
    const remainder = intNum % 1000;
    if (remainder > 0)
      result += " " + tFunc("progressBills.numberWords.and") + " ";
    intNum = remainder;
  }
  if (intNum > 0) {
    result += convertThreeDigits(intNum);
  }

  if (decimalPart && parseInt(decimalPart, 10) > 0) {
    result +=
      " " +
      tFunc("progressBills.numberWords.and") +
      " " +
      convertThreeDigits(parseInt(decimalPart, 10)) +
      " " +
      tFunc("progressBills.numberWords.qirsh");
  }

  return result || tFunc("progressBills.numberWords.zero");
};

/** Max rows per A4 sheet (portrait 794×1123) — header, title, padding, footer reserved */
const ALL_PROGRESS_BILLS_ROWS_PER_PAGE = 12;

const chunkProgressBillsPages = (items, pageSize) => {
  const safeItems = Array.isArray(items) ? items : [];
  if (!safeItems.length) return [];
  const chunks = [];
  for (let i = 0; i < safeItems.length; i += pageSize) {
    chunks.push(safeItems.slice(i, i + pageSize));
  }
  return chunks;
};

/**
 * One printable A4 page: repeating header/footer, single merged table, no absolute footer overlap.
 * Used for html2canvas (one canvas per page) and screen preview.
 */
const AllProgressBillsPageSheet = ({
  bills,
  pageIndex,
  totalPages,
  isRTL,
  t,
  filterText,
  formatDate,
  formatCurrency,
  formatContract,
  getStatusText,
  publicUrl,
  headerLogo,
  headerImageSrc,
  className,
  sheetStyle,
}) => {
  const getProjectNameForPdf = bill => {
    const project = bill?.project;
    if (!project) return "—";
    return isRTL
      ? project.name_ar || project.name_en || "—"
      : project.name_en || project.name_ar || "—";
  };

  const getCustomerNameForPdf = bill => {
    if (!bill) return "—";
    if (bill.customer_name) return bill.customer_name;
    const customer = bill.project?.customer || bill.contract?.customer;
    if (!customer) return "—";
    return isRTL
      ? customer.name_ar || customer.name_en || customer.name || "—"
      : customer.name_en || customer.name || customer.name_ar || "—";
  };

  const baseSheet = {
    width: "794px",
    height: "1123px",
    maxHeight: "1123px",
    boxSizing: "border-box",
    padding: "32px 40px 36px",
    backgroundColor: "#ffffff",
    color: "#000000",
    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
    fontWeight: "bold",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
    direction: isRTL ? "rtl" : "ltr",
  };

  return (
    <div className={className} style={{ ...baseSheet, ...sheetStyle }}>
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            flex: 1,
            minHeight: 0,
            padding: "14px 16px 12px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}
          >
            <img
              src={headerImageSrc}
              alt=""
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "6px",
                borderBottom: "4px double #000",
                paddingBottom: "6px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                paddingTop: "4px",
                paddingBottom: "8px",
              }}
            >
              <h1
                style={{
                  textAlign: "center",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  fontSize: "18px",
                  margin: "4px 0 2px",
                  flexShrink: 0,
                  color: "#000080",
                }}
              >
                {t("progressBills.title", { defaultValue: "المستخلصات" })}
              </h1>
              {pageIndex === 0 && (
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "11px",
                    margin: "0 0 2px",
                    fontFamily: '"Times New Roman", serif',
                    fontWeight: "normal",
                    color: "#333",
                    flexShrink: 0,
                  }}
                >
                  {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}
              {pageIndex === 0 && filterText && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "10px",
                    margin: "0 0 4px",
                    color: "#555",
                    direction: isRTL ? "rtl" : "ltr",
                    fontFamily: '"Times New Roman", serif',
                    fontWeight: "normal",
                    flexShrink: 0,
                  }}
                >
                  {filterText}
                </div>
              )}
              {totalPages > 1 && (
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "11px",
                    margin: "0 0 8px",
                    fontFamily: '"Times New Roman", "Times", serif',
                    fontWeight: "normal",
                    flexShrink: 0,
                  }}
                >
                  {isRTL
                    ? `صفحة ${pageIndex + 1} من ${totalPages}`
                    : `Page ${pageIndex + 1} of ${totalPages}`}
                </p>
              )}

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  direction: isRTL ? "rtl" : "ltr",
                  fontFamily: '"Times New Roman", "Times", serif',
                  fontWeight: "normal",
                  backgroundColor: "rgba(255, 255, 255, 0.75)",
                  padding: "10px",
                  borderRadius: "4px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <table
                  className="all-progress-bills-print-table"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "10px",
                    border: "1px solid #000",
                    fontFamily: '"Times New Roman", "Times", serif',
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr
                      style={{ backgroundColor: "rgba(238, 242, 247, 0.85)" }}
                    >
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "36px",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "10%",
                        }}
                      >
                        {t("progressBills.pdf.column.billNo")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "19%",
                        }}
                      >
                        {t("progressBills.pdf.column.contract")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "18%",
                        }}
                      >
                        {t("progressBills.project", {
                          defaultValue: "المشروع",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "16%",
                        }}
                      >
                        {t("progressBills.customer", {
                          defaultValue: "العميل",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "11%",
                        }}
                      >
                        {t("progressBills.pdf.column.date")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "9%",
                        }}
                      >
                        {t("progressBills.pdf.column.totalAmount")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "9%",
                        }}
                      >
                        {t("progressBills.pdf.column.netAmount")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: "700",
                          width: "8%",
                        }}
                      >
                        {t("progressBills.pdf.column.status")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((bill, idx) => {
                      const contractName = formatContract(bill.contract);
                      const projectName = getProjectNameForPdf(bill);
                      const customerName = getCustomerNameForPdf(bill);
                      const statusText = getStatusText(bill.status);
                      const bg =
                        idx % 2 === 0
                          ? "rgba(255, 255, 255, 0.85)"
                          : "rgba(248, 250, 252, 0.9)";
                      return (
                        <tr
                          key={`${bill.id}-${pageIndex}-${idx}`}
                          style={{ backgroundColor: bg }}
                        >
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              textAlign: "center",
                              width: "36px",
                            }}
                          >
                            {pageIndex * ALL_PROGRESS_BILLS_ROWS_PER_PAGE +
                              idx +
                              1}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              textAlign: "center",
                              wordBreak: "break-word",
                            }}
                          >
                            {bill.bill_no || bill.id || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              wordBreak: "break-word",
                            }}
                          >
                            {contractName}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              wordBreak: "break-word",
                            }}
                          >
                            {projectName}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              wordBreak: "break-word",
                            }}
                          >
                            {customerName}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              textAlign: "center",
                            }}
                          >
                            {formatDate(bill.date) || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              textAlign: "center",
                            }}
                          >
                            {formatCurrency(bill.total_amount)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              textAlign: "center",
                            }}
                          >
                            {formatCurrency(bill.net_amount)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "5px 4px",
                              textAlign: "center",
                              wordBreak: "break-word",
                            }}
                          >
                            {statusText}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ flexShrink: 0, marginTop: "8px", paddingTop: "4px" }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt=""
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Print Template for All Progress Bills
const AllProgressBillsPrintTemplate = ({
  progressBills,
  isRTL,
  t,
  filterText,
  formatDate,
  formatCurrency,
  formatContract,
  getStatusText,
  publicUrl,
  headerLogo,
}) => {
  if (!progressBills || progressBills.length === 0) return null;

  const chunks = chunkProgressBillsPages(
    progressBills,
    ALL_PROGRESS_BILLS_ROWS_PER_PAGE,
  );
  const totalPages = chunks.length;

  return (
    <div
      id="progress-bills-pdf-all"
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-9999px",
        top: 0,
        width: "794px",
        backgroundColor: "#ffffff",
        overflow: "visible",
      }}
    >
      {chunks.map((chunk, pageIndex) => (
        <AllProgressBillsPageSheet
          key={pageIndex}
          bills={chunk}
          pageIndex={pageIndex}
          totalPages={totalPages}
          isRTL={isRTL}
          t={t}
          filterText={filterText}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          formatContract={formatContract}
          getStatusText={getStatusText}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          headerImageSrc={`${publicUrl}/topHeader.png`}
          className="progress-bills-print-page"
        />
      ))}
    </div>
  );
};

// Preview Template Component for Single Progress Bill
const ProgressBillPreviewTemplate = ({
  bill,
  billDetails,
  isRTL,
  i18n,
  t,
  formatDate,
  formatCurrency,
  formatContract,
  getStatusText,
  convertNumberToArabicWords,
  publicUrl,
  headerLogo,
  backgroundLogo,
}) => {
  if (!bill || !billDetails) return null;

  const dir = isRTL ? "rtl" : "ltr";
  const textAlign = isRTL ? "right" : "left";

  const summary = Array.isArray(billDetails.invoice_summary)
    ? billDetails.invoice_summary
    : [];
  const isRunning = billDetails.bill_category
    ? billDetails.bill_category === "running"
    : summary.length > 0
      ? !summary.some(
          r => r.key === "previous_payments" && parseFloat(r.value) > 0,
        )
      : billDetails.type !== "bill"; // claim without summary = running-like
  const isBill = billDetails.type === "bill";
  const isFinal =
    billDetails.bill_category === "final" ||
    (isBill &&
      summary.some(
        r => r.key === "previous_payments" && parseFloat(r.value) > 0,
      ));
  const totalCells = 12;
  const fmtQty = n => {
    const num = parseFloat(n) || 0;
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  };
  const getSummaryValue = key => {
    const row = summary.find(r => r.key === key);
    return parseFloat(row ? row.value : 0);
  };
  const netAmount = getSummaryValue("net_amount");

  return (
    <>
      <div
        style={{
          width: "1123px",
          minHeight: "794px",
          height: "auto",
          backgroundColor: "#ffffff",
          color: "#000000",
          fontFamily:
            '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: "bold",
          padding: "10px",
          boxSizing: "border-box",
          margin: "0 auto",
          overflow: "visible",
        }}
      >
        <div
          style={{
            height: "100%",
            minHeight: "794px",
            padding: "18px 18px 0",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: i18n?.language === "ar" ? "rtl" : "ltr",
          }}
        >
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
              justifyContent: "space-between",
            }}
          >
            {/* Header */}
            <img
              src={`${publicUrl}/topHeader.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                position: "relative",
                zIndex: 2,
              }}
            >
              {/* Title */}
              <div
                style={{
                  marginTop: "20px",
                  textAlign: "center",
                  direction: i18n?.language === "ar" ? "rtl" : "ltr",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#000080",
                    marginBottom: "8px",
                  }}
                >
                  {t("progressBills.pdf.title")}{" "}
                  {billDetails.bill_no || bill.bill_no || `#${bill.id}`}
                </div>
              </div>

              {/* Info Section - صفوف منفصلة بدلاً من جدول */}
              <div
                style={{
                  marginTop: "12px",
                  direction: i18n?.language === "ar" ? "rtl" : "ltr",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  justifyContent: "space-between",
                }}
              >
                {/* Right Side - التاريخ، رقم الفاتورة، اسم العميل، اسم المشروع، ملاحظات */}
                <div
                  style={{
                    flex: "1 1 45%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {billDetails.date && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "14px",
                          color: "#000080",
                          minWidth: "100px",
                        }}
                      >
                        {t("progressBills.pdf.date")}:
                      </span>
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "15px",
                          color: "#000080",
                        }}
                      >
                        {formatDate(billDetails.date)}
                      </span>
                    </div>
                  )}
                  {billDetails.invoice_number && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "14px",
                          color: "#000080",
                          minWidth: "100px",
                        }}
                      >
                        {t("progressBills.pdf.invoiceNumber", {
                          defaultValue: "رقم الفاتورة",
                        })}
                        :
                      </span>
                      <span
                        style={{
                          fontWeight: "500",
                          fontSize: "15px",
                          color: "#000",
                        }}
                      >
                        {billDetails.invoice_number}
                      </span>
                    </div>
                  )}
                  {(billDetails.contract?.customer?.name_ar ||
                    billDetails.contract?.customer?.name_en ||
                    billDetails.project?.customer?.name_ar ||
                    billDetails.project?.customer?.name_en) && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "14px",
                          color: "#000080",
                          minWidth: "100px",
                        }}
                      >
                        {t("progressBills.pdf.clientName")}:
                      </span>
                      <span
                        style={{
                          fontWeight: "500",
                          fontSize: "15px",
                          color: "#000",
                        }}
                      >
                        {billDetails.contract?.customer?.name_ar ||
                          billDetails.contract?.customer?.name_en ||
                          billDetails.project?.customer?.name_ar ||
                          billDetails.project?.customer?.name_en}
                      </span>
                    </div>
                  )}
                  {(billDetails.project?.name_ar ||
                    billDetails.project?.name_en) && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "14px",
                          color: "#000080",
                          minWidth: "100px",
                        }}
                      >
                        {t("progressBills.pdf.projectName")}:
                      </span>
                      <span
                        style={{
                          fontWeight: "500",
                          fontSize: "15px",
                          color: "#000",
                        }}
                      >
                        {billDetails.project?.name_ar ||
                          billDetails.project?.name_en}
                      </span>
                    </div>
                  )}
                  {billDetails.notes && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "14px",
                          color: "#000080",
                          minWidth: "100px",
                        }}
                      >
                        {t("progressBills.pdf.notes")}:
                      </span>
                      <span
                        style={{
                          fontWeight: "500",
                          fontSize: "15px",
                          color: "#000080",
                        }}
                      >
                        {billDetails.notes}
                      </span>
                    </div>
                  )}
                </div>

                {/* Left Side - نوع المستخلص، خلال الفترة من، إلى */}
                <div
                  style={{
                    flex: "1 1 45%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "700",
                        fontSize: "14px",
                        color: "#000080",
                      }}
                    >
                      {t("progressBills.pdf.billType")}:
                    </span>
                    <span
                      style={{
                        fontWeight: "500",
                        fontSize: "15px",
                        color: "#000080",
                      }}
                    >
                      {billDetails.type === "bill"
                        ? i18n?.language === "ar"
                          ? "فاتورة"
                          : "Bill"
                        : billDetails.type === "claim"
                          ? i18n?.language === "ar"
                            ? "مطالبة"
                            : "Claim"
                          : "—"}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "700",
                        fontSize: "14px",
                        color: "#000080",
                      }}
                    >
                      {t("progressBills.print.billCategory", {
                        defaultValue: "فئة المستخلص",
                      })}
                      :
                    </span>
                    <span
                      style={{
                        fontWeight: "500",
                        fontSize: "15px",
                        color: "#000080",
                      }}
                    >
                      {billDetails.bill_category_label ||
                        (billDetails.bill_category === "running"
                          ? i18n?.language === "ar"
                            ? "جاري"
                            : "Running"
                          : billDetails.bill_category === "final"
                            ? i18n?.language === "ar"
                              ? "ختامي"
                              : "Final"
                            : isRunning
                              ? i18n?.language === "ar"
                                ? "جاري"
                                : "Running"
                              : i18n?.language === "ar"
                                ? "ختامي"
                                : "Final")}
                    </span>
                  </div>
                  {billDetails.period_start && billDetails.period_end && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "14px",
                          color: "#000080",
                        }}
                      >
                        {t("progressBills.pdf.periodFrom")}:
                      </span>
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "15px",
                          color: "#000080",
                        }}
                      >
                        {formatDate(billDetails.period_start)}
                      </span>
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "14px",
                          color: "#000080",
                        }}
                      >
                        {t("progressBills.pdf.periodTo")}:
                      </span>
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "15px",
                          color: "#000080",
                        }}
                      >
                        {formatDate(billDetails.period_end)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Background Logo - فوق الجدول */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.05,
                  zIndex: 3,
                  pointerEvents: "none",
                }}
              />

              {/* Items Table and Financial Summary Side by Side */}
              {billDetails.items && billDetails.items.length > 0 && (
                <div
                  style={{
                    marginTop: "18px",
                    direction: i18n?.language === "ar" ? "rtl" : "ltr",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  {/* Items Table - على اليسار */}
                  <div style={{ flex: "1 1 60%" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "12px",
                        border: "1px solid #000",
                        borderSpacing: 0,
                        margin: 0,
                        padding: 0,
                        cellSpacing: 0,
                        cellPadding: 0,
                        position: "relative",
                        zIndex: 2,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: "#ffffff" }}>
                          <th
                            style={{
                              border: "1px solid #000",
                              borderBottom: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "center",
                              fontWeight: "700",
                              margin: 0,
                              borderCollapse: "collapse",
                              fontSize: "12px",
                              lineHeight: "1.3",
                              width: "5%",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            {t("progressBills.pdf.table1.itemNumber", {
                              defaultValue: "رقم البند",
                            })}
                          </th>
                          <th
                            colSpan="4"
                            style={{
                              border: "1px solid #000",
                              borderBottom: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "center",
                              fontWeight: "700",
                              margin: 0,
                              borderCollapse: "collapse",
                              fontSize: "12px",
                              lineHeight: "1.3",
                            }}
                          >
                            {t("progressBills.pdf.table1.workDescription", {
                              defaultValue: "بيان الاعمال",
                            })}
                          </th>
                          {isFinal ? (
                            <>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t(
                                  "progressBills.pdf.table1.executedQuantity",
                                  { defaultValue: "الكمية المنفذة" },
                                )}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t(
                                  "progressBills.pdf.table1.contractQuantity",
                                  { defaultValue: "الكمية بالعقد" },
                                )}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t(
                                  "progressBills.pdf.table1.previouslyExecuted",
                                  { defaultValue: "ما سبق تنفيذه" },
                                )}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t("progressBills.pdf.table1.currentQuantity", {
                                  defaultValue: "الكمية الحالية",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t("progressBills.pdf.table1.totalExecuted", {
                                  defaultValue: "إجمالي المنفذ",
                                })}
                              </th>
                            </>
                          ) : (
                            <>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t(
                                  "progressBills.pdf.table1.executedQuantity",
                                  { defaultValue: "الكمية المنفذة" },
                                )}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t(
                                  "progressBills.pdf.table1.contractQuantity",
                                  { defaultValue: "الكمية بالعقد" },
                                )}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t(
                                  "progressBills.pdf.table1.previouslyExecuted",
                                  { defaultValue: "ما سبق تنفيذه" },
                                )}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t(
                                  "progressBills.pdf.table1.remainingQuantity",
                                  { defaultValue: "الكمية المتبقية" },
                                )}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  borderBottom: "1px solid #000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  margin: 0,
                                  borderCollapse: "collapse",
                                  fontSize: "12px",
                                  lineHeight: "1.3",
                                  width: "8%",
                                }}
                              >
                                {t("progressBills.percentage", {
                                  defaultValue: "نسبة الاتمام",
                                })}
                              </th>
                            </>
                          )}
                          <th
                            style={{
                              border: "1px solid #000",
                              borderBottom: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "center",
                              fontWeight: "700",
                              margin: 0,
                              borderCollapse: "collapse",
                              fontSize: "12px",
                              lineHeight: "1.3",
                              width: "8%",
                            }}
                          >
                            {t("progressBills.pdf.table1.price", {
                              defaultValue: "السعر",
                            })}
                          </th>
                          {/* Removed contract total column to match images */}
                          <th
                            style={{
                              border: "1px solid #000",
                              borderBottom: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "center",
                              fontWeight: "700",
                              margin: 0,
                              borderCollapse: "collapse",
                              fontSize: "12px",
                              lineHeight: "1.3",
                              width: "8%",
                            }}
                          >
                            {t("progressBills.pdf.table1.total", {
                              defaultValue: "الاجمالي",
                            })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {billDetails.items.map((item, idx) => {
                          const contractItem = item.contract_item || {};
                          const cQty = parseFloat(contractItem.quantity || 0);
                          const pQty = getPreviouslyExecutedQtyFromItem(item);
                          const curQty = getCurrentQtyFromItem(item);
                          const tQty = parseFloat(item.total_qty || 0);
                          const price = parseFloat(
                            contractItem.unit_price || 0,
                          );
                          const amt = parseFloat(item.amount || 0);
                          const rQty = cQty - tQty;
                          const pct =
                            cQty > 0 ? Math.round((tQty / cQty) * 100) : 0;

                          const rowTdCell = {
                            border: "1px solid #000",
                            padding: "4px 6px",
                            textAlign: "center",
                            margin: 0,
                            borderCollapse: "collapse",
                            fontSize: "12px",
                            lineHeight: "1.3",
                            backgroundColor: "#ffffff",
                          };
                          return (
                            <tr
                              key={item.id || idx}
                              style={{ backgroundColor: "#ffffff" }}
                            >
                              <td style={rowTdCell}>{idx + 1}</td>
                              <td
                                colSpan="4"
                                style={{
                                  ...rowTdCell,
                                  direction:
                                    i18n?.language === "ar" ? "rtl" : "ltr",
                                  textAlign: "center",
                                }}
                              >
                                {i18n?.language === "ar"
                                  ? contractItem.description_ar ||
                                    contractItem.description_en ||
                                    "—"
                                  : contractItem.description_en ||
                                    contractItem.description_ar ||
                                    "—"}
                              </td>
                              {isFinal ? (
                                <>
                                  <td style={rowTdCell}>{fmtQty(curQty)}</td>
                                  <td style={rowTdCell}>{fmtQty(cQty)}</td>
                                  <td style={rowTdCell}>{fmtQty(pQty)}</td>
                                  <td style={rowTdCell}>{fmtQty(curQty)}</td>
                                  <td style={rowTdCell}>{fmtQty(tQty)}</td>
                                  <td style={rowTdCell}>
                                    {formatCurrency(price)}
                                  </td>
                                  <td style={rowTdCell}>
                                    {formatCurrency(amt)}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td style={rowTdCell}>{fmtQty(curQty)}</td>
                                  <td style={rowTdCell}>{fmtQty(cQty)}</td>
                                  <td style={rowTdCell}>{fmtQty(pQty)}</td>
                                  <td style={rowTdCell}>{fmtQty(rQty)}</td>
                                  <td style={rowTdCell}>{pct}%</td>
                                  <td style={rowTdCell}>
                                    {formatCurrency(price)}
                                  </td>
                                  <td style={rowTdCell}>
                                    {formatCurrency(amt)}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                        {/* Financial Summary */}
                        {summary.map((row, si) => {
                          const rowTd2 = {
                            border: "1px solid #000",
                            padding: "4px 6px",
                            textAlign: "center",
                            direction: dir,
                            fontWeight: "700",
                            margin: 0,
                            borderCollapse: "collapse",
                            fontSize: "12px",
                            lineHeight: "1.3",
                            backgroundColor: "#ffffff",
                          };
                          const rowTd3 = {
                            border: "1px solid #000",
                            padding: "4px 6px",
                            textAlign: "center",
                            fontWeight: "700",
                            margin: 0,
                            borderCollapse: "collapse",
                            fontSize: "12px",
                            lineHeight: "1.3",
                            backgroundColor: "#ffffff",
                          };
                          return (
                            <tr
                              key={row.key || si}
                              style={{ backgroundColor: "#ffffff" }}
                            >
                              <td colSpan={totalCells - 1} style={rowTd2}>
                                {row.label}
                              </td>
                              <td style={rowTd3}>
                                {formatCurrency(row.value)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Bill Value Text - Above Signatures */}
              {netAmount > 0 &&
                typeof convertNumberToArabicWords === "function" && (
                  <div
                    style={{
                      marginTop: "20px",
                      marginBottom: "10px",
                      direction: i18n?.language === "ar" ? "rtl" : "ltr",
                      textAlign: "center",
                      fontSize: "14px",
                      lineHeight: 1.8,
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    <div style={{ fontWeight: "700", color: "#000080" }}>
                      {i18n?.language === "ar"
                        ? "قيمة المستخلص فقط وقدره: "
                        : "Bill value only: "}
                      {convertNumberToArabicWords(
                        parseFloat(netAmount || 0),
                        t,
                      )}{" "}
                      {i18n?.language === "ar"
                        ? t("progressBills.currency.egp")
                        : "EGP"}
                    </div>
                  </div>
                )}

              {/* Signatures Section */}
              <div
                style={{
                  marginTop: "20px",
                  marginBottom: "20px",
                  direction: i18n?.language === "ar" ? "rtl" : "ltr",
                  fontSize: "13px",
                  lineHeight: 1.8,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "20px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{ flex: 1, minWidth: "200px", textAlign: "center" }}
                  >
                    <div
                      style={{
                        color: "#000080",
                        fontWeight: "700",
                        fontSize: "13px",
                        marginBottom: "8px",
                      }}
                    >
                      {i18n?.language === "ar"
                        ? "توقيع المحاسب"
                        : "Accountant Signature"}
                    </div>
                    <div
                      style={{
                        paddingTop: "5px",
                        fontSize: "10px",
                        letterSpacing: "1px",
                        color: "#000",
                      }}
                    >
                      ........................................
                    </div>
                  </div>

                  <div
                    style={{ flex: 1, minWidth: "200px", textAlign: "center" }}
                  >
                    <div
                      style={{
                        color: "#000080",
                        fontWeight: "700",
                        fontSize: "13px",
                        marginBottom: "8px",
                      }}
                    >
                      {i18n?.language === "ar"
                        ? "توقيع المراجعة"
                        : "Review Signature"}
                    </div>
                    <div
                      style={{
                        paddingTop: "5px",
                        fontSize: "10px",
                        letterSpacing: "1px",
                        color: "#000",
                      }}
                    >
                      ........................................
                    </div>
                  </div>

                  <div
                    style={{ flex: 1, minWidth: "200px", textAlign: "center" }}
                  >
                    <div
                      style={{
                        color: "#000080",
                        fontWeight: "700",
                        fontSize: "13px",
                        marginBottom: "8px",
                      }}
                    >
                      {i18n?.language === "ar"
                        ? "توقيع الاعتماد"
                        : "Approval Signature"}
                    </div>
                    <div
                      style={{
                        paddingTop: "5px",
                        fontSize: "10px",
                        letterSpacing: "1px",
                        color: "#000",
                      }}
                    >
                      ........................................
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="print-footer"
            style={{
              marginTop: "auto",
              flexShrink: 0,
              position: "relative",
              zIndex: 2,
              alignSelf: "stretch",
            }}
          >
            <div
              style={{
                borderTop: "2px solid #000",
                padding: "10px",
                direction: i18n?.language === "ar" ? "rtl" : "ltr",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                marginBottom: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                {/* تاريخ الطباعة والوقت - على الشمال */}
                <div
                  style={{
                    direction: i18n?.language === "ar" ? "ltr" : "ltr",
                    fontSize: "12px",
                    color: "#000080",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div
                    style={{
                      textAlign: i18n?.language === "ar" ? "left" : "left",
                    }}
                  >
                    {t("progressBills.pdf.footer.printDate")}:{" "}
                    {formatDate(new Date())}
                  </div>
                  <div
                    style={{
                      textAlign: i18n?.language === "ar" ? "center" : "left",
                    }}
                  >
                    {t("progressBills.pdf.footer.time")}:{" "}
                    {new Date().toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                {/* المقر الرئيسي - متسنتر في النص */}
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontWeight: "700", color: "#000080" }}>
                    {t("progressBills.pdf.footer.headquarters")}
                  </div>
                  <div style={{ color: "#000080" }}>
                    {t("progressBills.pdf.footer.address")}
                  </div>
                  <div style={{ color: "#000080", fontWeight: "700" }}>
                    {t("progressBills.pdf.footer.contact")}
                  </div>
                </div>
                {/* User Name - على اليمين */}
                <div
                  style={{
                    textAlign: i18n?.language === "ar" ? "right" : "right",
                    direction: i18n?.language === "ar" ? "rtl" : "ltr",
                    fontSize: "12px",
                    color: "#000080",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div>
                    {t("progressBills.pdf.footer.userName")}:{" "}
                    {billDetails.creator?.name || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// All Progress Bills Preview Template Component
const AllProgressBillsPreviewTemplate = ({
  progressBills,
  isRTL,
  t,
  filterText,
  formatDate,
  formatCurrency,
  formatContract,
  getStatusText,
  publicUrl,
  headerLogo,
}) => {
  if (!progressBills || progressBills.length === 0) return null;

  const chunks = chunkProgressBillsPages(
    progressBills,
    ALL_PROGRESS_BILLS_ROWS_PER_PAGE,
  );
  const totalPages = chunks.length;

  return (
    <div
      style={{
        width: "794px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "28px",
        alignItems: "center",
      }}
    >
      {chunks.map((chunk, pageIndex) => (
        <AllProgressBillsPageSheet
          key={pageIndex}
          bills={chunk}
          pageIndex={pageIndex}
          totalPages={totalPages}
          isRTL={isRTL}
          t={t}
          filterText={filterText}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          formatContract={formatContract}
          getStatusText={getStatusText}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          headerImageSrc={`${publicUrl}/HeaderAce.png`}
          sheetStyle={{
            boxShadow: "0 2px 14px rgba(0, 0, 0, 0.08)",
          }}
        />
      ))}
    </div>
  );
};

export default ProgressBill;
